import { useState, useEffect, useRef, useMemo } from 'react';
import type { PageContent, ContentSection } from '@/mocks/websiteBuilder';
import type { GlobalContactInfo } from '../page';
import {
  normalizeCustomSection,
  serializeCustomSection,
  createDefaultBlock,
  MAX_CUSTOM_BLOCKS,
  type CustomSectionLayout,
  type CustomSectionBlock,
} from '../../../utils/customSectionUtils';
import HeroEditor from './HeroEditor';
import GalleryEditor from './GalleryEditor';
import ServiceProductSection from './ServiceProductSection';
import IconPicker from '@/components/base/IconPicker';
import CharLimitLabel from '@/components/base/CharLimitLabel';
import RichTextEditor from '@/components/base/RichTextEditor';
import ImageUploader from '@/components/base/ImageUploader';
import type React from 'react';
import api from '@/services/api';
import { usePageTracking } from '../../../hooks/usePageTracking';
import { useTelemetry } from '../../../hooks/useTelemetry';
import { MODULES } from '../../../telemetry/constants/modules';
import { ACTIONS } from '../../../telemetry/constants/actions';

const SECTION_DISPLAY_NAMES: Record<string, string> = {
  usp: 'USP', grid: 'Differentiators', clientLogos: 'Client Logos', clientlogo: 'Client Logos',
  problemSolution: 'Problem Solution', problemsolution: 'Problem Solution', serviceCard: 'Service Cards', servicecard: 'Service Cards',
  productCard: 'Product Cards', productcard: 'Product Cards', founder: 'Founder', team: 'Team',
  gallery: 'Gallery', testimonial: 'Testimonials', faq: 'FAQ',
  blog: 'Blog', story: 'Story', hero: 'Hero', contact: 'Contact', contactInfo: 'Contact Info', contactForm: 'Contact Form',
  custom: 'Custom Section', customSection: 'Custom Section'
};

export interface SectionTemplate {
  type: string;
  name: string;
  category: 'Hero' | 'Features & Content' | 'About & Team' | 'Products & Services' | 'Proof & Trust' | 'Contact & Info' | 'Custom';
  description: string;
  icon: string;
  defaultTitle: string;
  defaultSubtitle?: string;
  defaultContent: any;
}

export const SECTION_TEMPLATES: SectionTemplate[] = [
  {
    type: 'hero',
    name: 'Hero Banner',
    category: 'Hero',
    description: 'High-impact banner with headline, subtext, call-to-action button, and background image.',
    icon: 'ri-flag-line',
    defaultTitle: 'Hero Banner',
    defaultSubtitle: '',
    defaultContent: [
      JSON.stringify({
        headline: 'Empower Your Business Growth',
        sub: 'Deliver innovative solutions with speed, precision, and excellence.',
        cta: 'Get Started',
        image: ''
      })
    ]
  },
  {
    type: 'custom',
    name: 'Custom Section',
    category: 'Custom',
    description: 'A flexible section with custom heading, rich text paragraphs, and optional image.',
    icon: 'ri-edit-circle-line',
    defaultTitle: 'New Custom Section',
    defaultSubtitle: 'Custom Highlights',
    defaultContent: serializeCustomSection('story', [
      {
        id: 'block_1',
        title: '',
        body: '<p>Add your custom content, announcements, specialized messaging, or detailed overviews here.</p>',
        image: '',
        icon: 'ri-star-line'
      }
    ])
  },
  {
    type: 'usp',
    name: 'Key Features / USPs',
    category: 'Features & Content',
    description: 'List of key features or unique selling propositions with icons.',
    icon: 'ri-sparkling-fill',
    defaultTitle: 'Why Choose Us',
    defaultSubtitle: 'Key Features',
    defaultContent: [
      JSON.stringify({ title: 'Fast & Reliable', description: 'Engineered for optimal speed and uptime.', icon: 'ri-flashlight-line' }),
      JSON.stringify({ title: 'Enterprise Security', description: 'Built with rigorous data protection standards.', icon: 'ri-shield-check-line' }),
      JSON.stringify({ title: '24/7 Dedicated Support', description: 'Our expert team is always ready to assist.', icon: 'ri-customer-service-2-line' })
    ]
  },
  {
    type: 'grid',
    name: 'Differentiators Grid',
    category: 'Features & Content',
    description: 'Grid cards showcasing unique differentiators, pillars, or core competencies.',
    icon: 'ri-grid-fill',
    defaultTitle: 'Our Core Strengths',
    defaultSubtitle: 'Differentiators',
    defaultContent: [
      JSON.stringify({ title: 'Industry Expertise', description: 'Decades of combined practical domain experience.', icon: 'ri-medal-line' }),
      JSON.stringify({ title: 'Modern Technology', description: 'Leveraging cutting-edge tools to stay ahead.', icon: 'ri-cpu-line' }),
      JSON.stringify({ title: 'Client-Centric', description: 'Solutions tailored around your exact objectives.', icon: 'ri-heart-line' })
    ]
  },
  {
    type: 'story',
    name: 'Brand Story / Narrative',
    category: 'Features & Content',
    description: 'Engaging storytelling block sharing your mission, heritage, and vision.',
    icon: 'ri-book-open-line',
    defaultTitle: 'Our Journey & Mission',
    defaultSubtitle: 'Our Story',
    defaultContent: '<p>Founded with a bold mission to transform our industry, we have spent years pioneering solutions that create lasting impact for our clients worldwide.</p>'
  },
  {
    type: 'problemSolution',
    name: 'Problem & Solution',
    category: 'Features & Content',
    description: 'Direct comparison between customer challenges and your proven solutions.',
    icon: 'ri-scales-3-line',
    defaultTitle: 'The Challenges We Solve',
    defaultSubtitle: 'Problem & Solution',
    defaultContent: [
      JSON.stringify({
        headline: 'How We Transform Your Challenges',
        problems: [
          { pain: 'Inefficient workflows and slow turnarounds.', solution: 'Automated, streamlined delivery pipelines.' },
          { pain: 'Lack of dedicated expertise and guidance.', solution: 'Direct access to senior industry specialists.' }
        ]
      })
    ]
  },
  {
    type: 'serviceCard',
    name: 'Service Cards',
    category: 'Products & Services',
    description: 'Grid of specialized services with icons, descriptions, and CTA links.',
    icon: 'ri-service-line',
    defaultTitle: 'Our Services',
    defaultSubtitle: 'What We Deliver',
    defaultContent: [
      JSON.stringify({ name: 'Strategic Consulting', description: 'Actionable roadmaps designed to accelerate growth.', icon: 'ri-compass-3-line', url: '#' }),
      JSON.stringify({ name: 'Implementation', description: 'End-to-end execution with high standards.', icon: 'ri-tools-line', url: '#' })
    ]
  },
  {
    type: 'productCard',
    name: 'Product Cards',
    category: 'Products & Services',
    description: 'Product catalog cards with photos, descriptions, and purchase/details links.',
    icon: 'ri-shopping-bag-3-line',
    defaultTitle: 'Featured Products',
    defaultSubtitle: 'Our Catalog',
    defaultContent: [
      JSON.stringify({ name: 'Pro Edition', description: 'Complete suite built for growing businesses.', image: '', url: '#' }),
      JSON.stringify({ name: 'Enterprise Edition', description: 'Uncapped capability with custom integrations.', image: '', url: '#' })
    ]
  },
  {
    type: 'founder',
    name: 'Founder Profile',
    category: 'About & Team',
    description: 'Personalized spotlight for the founder with photo, title, and bio.',
    icon: 'ri-user-star-line',
    defaultTitle: 'Meet the Founder',
    defaultSubtitle: 'Leadership',
    defaultContent: [
      JSON.stringify({ name: 'Founder Name', role: 'Founder & CEO', bio: 'Passionate about building impactful solutions.', image: '' })
    ]
  },
  {
    type: 'team',
    name: 'Team Members',
    category: 'About & Team',
    description: 'Team roster showcasing team members, roles, headshots, and bios.',
    icon: 'ri-team-line',
    defaultTitle: 'Our Leadership Team',
    defaultSubtitle: 'People Behind The Brand',
    defaultContent: [
      JSON.stringify({ name: 'Sarah Jenkins', role: 'Head of Operations', bio: 'Driving execution and operational excellence.', image: '' }),
      JSON.stringify({ name: 'David Kumar', role: 'Technical Lead', bio: 'Architecting resilient and high-scale systems.', image: '' })
    ]
  },
  {
    type: 'testimonial',
    name: 'Client Testimonials',
    category: 'Proof & Trust',
    description: 'Social proof quotes with client names and organizational roles.',
    icon: 'ri-chat-quote-line',
    defaultTitle: 'What Our Clients Say',
    defaultSubtitle: 'Testimonials',
    defaultContent: [
      JSON.stringify({ quote: 'Working with this team completely transformed our digital presence and increased conversions.', client: 'Emily Roberts', role: 'VP Marketing' }),
      JSON.stringify({ quote: 'Exceptional attention to detail and unwavering support every step of the way.', client: 'Marcus Vance', role: 'Founder, Apex Inc.' })
    ]
  },
  {
    type: 'clientLogos',
    name: 'Client & Partner Logos',
    category: 'Proof & Trust',
    description: 'Logo strip highlighting notable clients, partners, and press mentions.',
    icon: 'ri-building-4-line',
    defaultTitle: 'Trusted by Industry Leaders',
    defaultSubtitle: 'Partners',
    defaultContent: [
      JSON.stringify({ name: 'Acme Corp', logo: '' }),
      JSON.stringify({ name: 'Global Tech', logo: '' }),
      JSON.stringify({ name: 'Nexus Partners', logo: '' })
    ]
  },
  {
    type: 'gallery',
    name: 'Image Gallery',
    category: 'Proof & Trust',
    description: 'Visual showcase of facilities, products, events, and portfolio works.',
    icon: 'ri-image-2-line',
    defaultTitle: 'Visual Showcase',
    defaultSubtitle: 'Gallery',
    defaultContent: []
  },
  {
    type: 'blog',
    name: 'Blog Articles',
    category: 'Features & Content',
    description: 'Articles and insights demonstrating domain authority and thought leadership.',
    icon: 'ri-article-line',
    defaultTitle: 'Latest Insights & Articles',
    defaultSubtitle: 'From Our Blog',
    defaultContent: [
      JSON.stringify({
        title: 'Top Industry Trends to Watch in 2026',
        excerpt: 'An overview of the biggest shifts shaping modern businesses today.',
        date: 'Oct 12, 2026',
        content: '<p>Comprehensive breakdown of modern industry trends and actionable takeaways to help your business stay competitive and ahead of the curve.</p><p>Embracing technology and data-backed strategies is vital for sustainable success.</p>',
        image: ''
      }),
      JSON.stringify({
        title: 'Maximizing Efficiency and Operational Agility',
        excerpt: 'Proven strategies to streamline workflows and deliver consistent results.',
        date: 'Oct 05, 2026',
        content: '<p>Operational excellence begins with clear communication, standardized processes, and modern tooling. Discover how top teams eliminate bottlenecks and scale seamlessly.</p><p>Continuous improvement ensures lasting performance.</p>',
        image: ''
      }),
      JSON.stringify({
        title: 'Building Long-Term Client Relationships and Trust',
        excerpt: 'Why client-centric approaches create enduring brand value and loyalty.',
        date: 'Sep 28, 2026',
        content: '<p>Trust is the foundation of every enduring client partnership. Learn how transparent communication and consistent value delivery turn customers into brand advocates.</p><p>Investing in client relationships yields high long-term dividends.</p>',
        image: ''
      })
    ]
  },
  {
    type: 'faq',
    name: 'FAQ Accordion',
    category: 'Contact & Info',
    description: 'Interactive question and answer list to address common inquiries.',
    icon: 'ri-question-answer-line',
    defaultTitle: 'Frequently Asked Questions',
    defaultSubtitle: 'Got Questions?',
    defaultContent: [
      JSON.stringify({ question: 'How do I get started with your services?', answer: 'Simply contact us through the inquiry form and our onboarding team will reach out within 24 hours.' }),
      JSON.stringify({ question: 'What is your typical turnaround time?', answer: 'Project timelines vary based on scope, but most standard engagements are completed within 2 to 4 weeks.' })
    ]
  },
  {
    type: 'contactForm',
    name: 'Contact Form',
    category: 'Contact & Info',
    description: 'Lead generation inquiry form with name, email, phone, company, and message fields.',
    icon: 'ri-mail-send-line',
    defaultTitle: 'Get in Touch',
    defaultSubtitle: 'Contact Us',
    defaultContent: JSON.stringify({ heading: 'Send Us a Message', subtext: 'Fill out the form below and we will get back to you promptly.' })
  },
  {
    type: 'contactInfo',
    name: 'Contact Details & Map',
    category: 'Contact & Info',
    description: 'Direct email, phone number, location address, and interactive map.',
    icon: 'ri-map-pin-user-line',
    defaultTitle: 'Our Contact Information',
    defaultSubtitle: 'Visit Or Call',
    defaultContent: JSON.stringify({ description: 'Reach out to our team directly via phone, email, or visit our office.' })
  }
];

// --- Content Section Character Limits ---
const LIMITS = {
  sectionTitle:    { min: 0,  max: 100 },
  sectionSubtitle: { min: 0,  max: 250 },
  uspTitle:     { min: 2,  max: 40 },
  uspDesc:      { min: 10, max: 150 },
  story:        { min: 50, max: 800 },
  gridTitle:    { min: 2,  max: 40 },
  gridDesc:     { min: 10, max: 150 },
  blogTitle:    { min: 5,  max: 80 },

  blogContent:  { min: 100, max: 5000 },
  blogDate:     { min: 5,  max: 30 },
  founderName:  { min: 2,  max: 50 },
  founderRole:  { min: 2,  max: 50 },
  founderBio:   { min: 10, max: 500 },
  teamName:     { min: 2,  max: 50 },
  teamRole:     { min: 2,  max: 50 },
  teamBio:      { min: 10, max: 200 },
  contact:      { min: 20, max: 500 },
  psHeadline:   { min: 10, max: 100 },
  psPain:       { min: 10, max: 120 },
  psSolution:   { min: 10, max: 200 },
  cardName:     { min: 2,  max: 50 },
  cardDesc:     { min: 10, max: 200 },
  cardUrl:      { min: 1,  max: 200 },
  testimonialQuote: { min: 20, max: 300 },
  testimonialClient: { min: 2, max: 50 },
  testimonialRole:   { min: 2,  max: 80 },
  faqQuestion:  { min: 10, max: 150 },
  faqAnswer:    { min: 20, max: 500 },
  clientName:   { min: 2,  max: 50 },
};

interface FieldError {
  message: string;
}

type SectionErrors = Record<string, Record<string, FieldError>>;

function validateSection(section: ContentSection, vals: Record<string, string>): SectionErrors {
  const errors: SectionErrors = {};
  const setErr = (key: string, message: string) => {
    errors[key] = { message };
  };

  if (section.type === 'story') {
    const text = vals.text ?? '';
    if (text.length < LIMITS.story.min) {
      setErr('text', `Story must be at least ${LIMITS.story.min} characters (currently ${text.length})`);
    }
  }

  if (section.type === 'usp' || section.type === 'grid') {
    const arr = Array.isArray(section.content) ? section.content : [];
    arr.forEach((_, idx) => {
      const titleVal = vals[`${idx}_title`] ?? '';
      const descVal = vals[`${idx}_desc`] ?? '';
      if (titleVal.length < (section.type === 'usp' ? LIMITS.uspTitle.min : LIMITS.gridTitle.min)) {
        setErr(`${idx}_title`, `Title must be at least ${section.type === 'usp' ? LIMITS.uspTitle.min : LIMITS.gridTitle.min} characters (currently ${titleVal.length})`);
      }
      if (descVal.length < (section.type === 'usp' ? LIMITS.uspDesc.min : LIMITS.gridDesc.min)) {
        setErr(`${idx}_desc`, `Description must be at least ${section.type === 'usp' ? LIMITS.uspDesc.min : LIMITS.gridDesc.min} characters (currently ${descVal.length})`);
      }
    });
  }

  if (section.type === 'blog') {
    const arr = Array.isArray(section.content) ? section.content : [];
    arr.forEach((item, idx) => {
      let parsed;
      try {
        parsed = JSON.parse(item);
      } catch {
        parsed = { title: '', image: '', date: '', content: '' };
      }
      const titleVal = parsed.title || '';
      const contentVal = parsed.content || '';
      const dateVal = parsed.date || '';
      if (titleVal.length < LIMITS.blogTitle.min) {
        setErr(`${idx}_title`, `Title must be at least ${LIMITS.blogTitle.min} characters (currently ${titleVal.length})`);
      }
      if (contentVal.length < LIMITS.blogContent.min) {
        setErr(`${idx}_content`, `Content must be at least ${LIMITS.blogContent.min} characters (currently ${contentVal.length})`);
      }
      if (dateVal.length < LIMITS.blogDate.min) {
        setErr(`${idx}_date`, `Date must be at least ${LIMITS.blogDate.min} characters (currently ${dateVal.length})`);
      }
    });
  }

  if (section.type === 'founder') {
    const nameVal = vals['0_name'] ?? '';
    const roleVal = vals['0_role'] ?? '';
    const bioVal = vals['0_bio'] ?? '';
    if (nameVal.length < LIMITS.founderName.min) {
      setErr('0_name', `Name must be at least ${LIMITS.founderName.min} characters (currently ${nameVal.length})`);
    }
    if (roleVal.length < LIMITS.founderRole.min) {
      setErr('0_role', `Role must be at least ${LIMITS.founderRole.min} characters (currently ${roleVal.length})`);
    }
    if (bioVal.length < LIMITS.founderBio.min) {
      setErr('0_bio', `Bio must be at least ${LIMITS.founderBio.min} characters (currently ${bioVal.length})`);
    }
  }

  if (section.type === 'team') {
    const arr = Array.isArray(section.content) ? section.content : [];
    arr.forEach((_, idx) => {
      const nameVal = vals[`${idx}_name`] ?? '';
      const roleVal = vals[`${idx}_role`] ?? '';
      const bioVal = vals[`${idx}_bio`] ?? '';
      if (nameVal.length < LIMITS.teamName.min) {
        setErr(`${idx}_name`, `Name must be at least ${LIMITS.teamName.min} characters (currently ${nameVal.length})`);
      }
      if (roleVal.length < LIMITS.teamRole.min) {
        setErr(`${idx}_role`, `Role must be at least ${LIMITS.teamRole.min} characters (currently ${roleVal.length})`);
      }
      if (bioVal.length < LIMITS.teamBio.min) {
        setErr(`${idx}_bio`, `Bio must be at least ${LIMITS.teamBio.min} characters (currently ${bioVal.length})`);
      }
    });
  }

  if (section.type === 'contact' || section.type === 'contactInfo') {
    const desc = vals.description ?? '';
    if (desc.length < LIMITS.contact.min) {
      setErr('description', `Description must be at least ${LIMITS.contact.min} characters (currently ${desc.length})`);
    }
    const email = vals.email ?? '';
    if (email.trim().length === 0) {
      setErr('email', 'Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErr('email', 'Invalid email address');
    }
    
    const phone = vals.phone ?? '';
    if (phone.trim().length === 0) {
      setErr('phone', 'Phone is required');
    } else if (!/^\d{10}$/.test(phone.replace(/[-+()\s]/g, ''))) {
      setErr('phone', 'Phone number must be exactly 10 digits');
    }
    
    const location = vals.location ?? '';
    if (location.trim().length === 0) setErr('location', 'Location is required');
  }

  if (section.type === 'problemSolution') {
    const headline = vals['ps_headline'] ?? '';
    if (headline.length < LIMITS.psHeadline.min) {
      setErr('ps_headline', `Headline must be at least ${LIMITS.psHeadline.min} characters (currently ${headline.length})`);
    }
    const arr = Array.isArray(section.content) ? section.content : [];
    let parsed;
    try { parsed = arr[0] ? JSON.parse(arr[0]) : { problems: [] }; } catch { parsed = { problems: [] }; }
    const problems = parsed.problems ?? [];
    problems.forEach((_: unknown, idx: number) => {
      const pain = vals[`ps_pain_${idx}`] ?? '';
      const sol = vals[`ps_sol_${idx}`] ?? '';
      if (pain.length < LIMITS.psPain.min) {
        setErr(`ps_pain_${idx}`, `Pain point must be at least ${LIMITS.psPain.min} characters (currently ${pain.length})`);
      }
      if (sol.length < LIMITS.psSolution.min) {
        setErr(`ps_sol_${idx}`, `Solution must be at least ${LIMITS.psSolution.min} characters (currently ${sol.length})`);
      }
    });
  }

  if (section.type === 'serviceCard' || section.type === 'productCard') {
    const arr = Array.isArray(section.content) ? section.content : [];
    arr.forEach((_, idx) => {
      const name = vals[`card_${idx}_name`] ?? '';
      const desc = vals[`card_${idx}_desc`] ?? '';
      if (name.length < LIMITS.cardName.min) {
        setErr(`card_${idx}_name`, `Name must be at least ${LIMITS.cardName.min} characters (currently ${name.length})`);
      }
      if (desc.length < LIMITS.cardDesc.min) {
        setErr(`card_${idx}_desc`, `Description must be at least ${LIMITS.cardDesc.min} characters (currently ${desc.length})`);
      }
    });
  }

  if (section.type === 'testimonial') {
    const arr = Array.isArray(section.content) ? section.content : [];
    arr.forEach((_, idx) => {
      const quote = vals[`test_${idx}_quote`] ?? '';
      const client = vals[`test_${idx}_client`] ?? '';
      const role = vals[`test_${idx}_role`] ?? '';
      if (quote.length < LIMITS.testimonialQuote.min) {
        setErr(`test_${idx}_quote`, `Quote must be at least ${LIMITS.testimonialQuote.min} characters (currently ${quote.length})`);
      }
      if (client.length < LIMITS.testimonialClient.min) {
        setErr(`test_${idx}_client`, `Name must be at least ${LIMITS.testimonialClient.min} characters (currently ${client.length})`);
      }
      if (role.length < LIMITS.testimonialRole.min) {
        setErr(`test_${idx}_role`, `Role must be at least ${LIMITS.testimonialRole.min} characters (currently ${role.length})`);
      }
    });
  }

  if (section.type === 'faq') {
    const arr = Array.isArray(section.content) ? section.content : [];
    arr.forEach((_, idx) => {
      const q = vals[`faq_${idx}_q`] ?? '';
      const a = vals[`faq_${idx}_a`] ?? '';
      if (q.length < LIMITS.faqQuestion.min) {
        setErr(`faq_${idx}_q`, `Question must be at least ${LIMITS.faqQuestion.min} characters (currently ${q.length})`);
      }
      if (a.length < LIMITS.faqAnswer.min) {
        setErr(`faq_${idx}_a`, `Answer must be at least ${LIMITS.faqAnswer.min} characters (currently ${a.length})`);
      }
    });
  }

  if (section.type === 'custom' || section.type === 'customSection') {
    const titleVal = (vals.section_title ?? section.title ?? '').trim();
    if (!titleVal) {
      setErr('section_title', 'Section Heading is required (* Compulsory)');
    }
  }

  return errors;
}

const deduplicatePages = (pagesList: PageContent[]): PageContent[] => {
  if (!Array.isArray(pagesList)) return [];
  const map = new Map<string, PageContent>();

  for (const page of pagesList) {
    let cleanId = page.pageId;
    if (cleanId === 'about-us') cleanId = 'about';
    if (cleanId === 'contact-us') cleanId = 'contact';
    if (cleanId === 'service-landing') cleanId = 'services';
    if (cleanId === 'product-landing') cleanId = 'products';

    let cleanName = page.pageName;
    if (cleanId === 'about') cleanName = 'About Us';
    if (cleanId === 'contact') cleanName = 'Contact Us';
    if (cleanId === 'services') cleanName = 'Services';
    if (cleanId === 'products') cleanName = 'Products';
    if (cleanId === 'home') cleanName = 'Home';
    if (cleanId === 'blog') cleanName = 'Blog';
    if (cleanId === 'gallery') cleanName = 'Gallery';

    const normalizedPage: PageContent = {
      ...page,
      pageId: cleanId,
      pageName: cleanName
    };

    if (map.has(cleanId)) {
      const existing = map.get(cleanId)!;
      if (normalizedPage.status === 'Generated' && existing.status !== 'Generated') {
        map.set(cleanId, normalizedPage);
      }
    } else {
      map.set(cleanId, normalizedPage);
    }
  }

  return Array.from(map.values());
};

export default function ContentPreview({
  pages,
  onPagesChange: originalOnPagesChange,
  brandName = 'Glamac',
  globalContactInfo,
  setGlobalContactInfo,
  isExported,
  onRequestUnlock,
  activePageId,
  onActivePageIdChange,
  onManualSave,
  saveStatus,
}: {
  pages: PageContent[];
  onPagesChange: React.Dispatch<React.SetStateAction<PageContent[]>>;
  brandName?: string;
  globalContactInfo: GlobalContactInfo;
  setGlobalContactInfo: React.Dispatch<React.SetStateAction<GlobalContactInfo>>;
  isExported?: boolean;
  onRequestUnlock?: () => void;
  activePageId?: string;
  onActivePageIdChange?: (pageId: string) => void;
  onManualSave?: (overridePages?: PageContent[], overrideContact?: GlobalContactInfo) => Promise<void>;
  saveStatus?: string;
}) {
  const { trackAction } = useTelemetry({ module: MODULES.WEBSITE_BUILDER, submodule: 'Content Canvas' });

  const onPagesChange: React.Dispatch<React.SetStateAction<PageContent[]>> = (updater) => {
    originalOnPagesChange(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const dedupedNext = deduplicatePages(next);
      return dedupedNext.map(nextPage => {
        const prevPage = prev.find(p => p.pageId === nextPage.pageId);
        if (prevPage && JSON.stringify(prevPage.sections) !== JSON.stringify(nextPage.sections)) {
           trackAction(ACTIONS.EDIT_PAGE, 'Edit Website Page Content', { pageId: nextPage.pageId, pageTitle: nextPage.title });
           return { ...nextPage, status: 'Edited' as const };
        }
        return nextPage;
      });
    });
  };

  const [internalSelectedPageId, setInternalSelectedPageId] = useState<string>(activePageId || 'home');
  const selectedPageId = activePageId !== undefined ? activePageId : internalSelectedPageId;
  const setSelectedPageId = (pageIdOrUpdater: string | ((prev: string) => string)) => {
    const nextPageId = typeof pageIdOrUpdater === 'function' ? pageIdOrUpdater(selectedPageId) : pageIdOrUpdater;
    setInternalSelectedPageId(nextPageId);
    if (onActivePageIdChange) {
      onActivePageIdChange(nextPageId);
    }
  };
  usePageTracking({
    moduleName: 'Website Builder',
    submoduleName: `Content / ${selectedPageId}`,
    routePath: `/website-builder#content/${selectedPageId}`,
  });
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const contentCanvasRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (pages.length > 0 && selectedPageId !== 'global-footer' && !pages.find(p => p.pageId === selectedPageId)) {
      setSelectedPageId(pages[0].pageId);
    }
  }, [pages, selectedPageId]);

  useEffect(() => {
    // Scroll to top when page changes
    if (contentCanvasRef.current) {
      contentCanvasRef.current.scrollTo(0, 0);
      setTimeout(() => {
        contentCanvasRef.current?.scrollTo(0, 0);
      }, 10);
    }
  }, [selectedPageId]);

  // Poll for background image generation completion
  useEffect(() => {
    let hasPending = false;
    for (const page of pages) {
      for (const section of page.sections) {
        if (Array.isArray(section.content)) {
          for (const item of section.content) {
            const parsed = parseJSONSafely(item, {});
            if (parsed.image_status === 'pending' || parsed.logo_status === 'pending') {
              hasPending = true;
              break;
            }
          }
        } else {
          const parsed = parseJSONSafely(section.content, {});
          if (parsed.image_status === 'pending' || parsed.logo_status === 'pending') {
            hasPending = true;
          }
        }
        if (hasPending) break;
      }
      if (hasPending) break;
    }

    if (hasPending) {
      let pollCount = 0;
      const timer = setInterval(async () => {
        pollCount += 1;
        try {
          const apiRes = await api.getPages();
          const backendPages = deduplicatePages(apiRes?.pages || (Array.isArray(apiRes) ? apiRes : []));
          if (backendPages.length > 0) {
            onPagesChange(prevPages => {
              return prevPages.map(localPage => {
                const backendPage = backendPages.find(bp => bp.pageId === localPage.pageId);
                if (!backendPage) return localPage;

                const updatedSections = localPage.sections.map(localSec => {
                  const backendSec = backendPage.sections.find(bs => bs.id === localSec.id || (bs.type === localSec.type && bs.title === localSec.title));
                  if (!backendSec) return localSec;

                  let updatedContent = localSec.content;
                  if (Array.isArray(localSec.content) && Array.isArray(backendSec.content)) {
                    let changed = false;
                    const nextContent = localSec.content.map((localItem, idx) => {
                      const backendItem = backendSec.content[idx];
                      if (!backendItem) return localItem;
                      const localParsed = parseJSONSafely(localItem, {});
                      const backendParsed = parseJSONSafely(backendItem, {});

                      let itemChanged = false;
                      const merged = { ...localParsed };
                      
                      // 1. Image URL completion or status change
                      if (backendParsed.image && String(backendParsed.image).startsWith('http') && localParsed.image !== backendParsed.image) {
                        merged.image = backendParsed.image;
                        merged.image_status = 'completed';
                        itemChanged = true;
                      } else if (backendParsed.image_status && backendParsed.image_status !== 'pending' && localParsed.image_status !== backendParsed.image_status) {
                        merged.image_status = backendParsed.image_status;
                        itemChanged = true;
                      }

                      // 2. Logo URL completion or status change
                      if (backendParsed.logo && String(backendParsed.logo).startsWith('http') && localParsed.logo !== backendParsed.logo) {
                        merged.logo = backendParsed.logo;
                        merged.logo_status = 'completed';
                        itemChanged = true;
                      } else if (backendParsed.logo_status && backendParsed.logo_status !== 'pending' && localParsed.logo_status !== backendParsed.logo_status) {
                        merged.logo_status = backendParsed.logo_status;
                        itemChanged = true;
                      }

                      // 3. Safety timeout: after 12 polls (60s), stop infinite spinning if still pending
                      if (pollCount >= 12 && merged.image_status === 'pending' && !merged.image) {
                        merged.image_status = 'failed';
                        itemChanged = true;
                      }

                      if (itemChanged) {
                        changed = true;
                        return typeof localItem === 'string' ? JSON.stringify(merged) : merged;
                      }
                      return localItem;
                    });
                    if (changed) updatedContent = nextContent;
                  } else if (typeof localSec.content === 'string' || (typeof localSec.content === 'object' && localSec.content !== null)) {
                    const localParsed = parseJSONSafely(localSec.content, {});
                    const backendParsed = parseJSONSafely(backendSec.content, {});
                    let itemChanged = false;
                    const merged = { ...localParsed };
                    
                    if (backendParsed.image && String(backendParsed.image).startsWith('http') && localParsed.image !== backendParsed.image) {
                      merged.image = backendParsed.image;
                      merged.image_status = 'completed';
                      itemChanged = true;
                    } else if (backendParsed.image_status && backendParsed.image_status !== 'pending' && localParsed.image_status !== backendParsed.image_status) {
                      merged.image_status = backendParsed.image_status;
                      itemChanged = true;
                    }

                    if (backendParsed.logo && String(backendParsed.logo).startsWith('http') && localParsed.logo !== backendParsed.logo) {
                      merged.logo = backendParsed.logo;
                      merged.logo_status = 'completed';
                      itemChanged = true;
                    } else if (backendParsed.logo_status && backendParsed.logo_status !== 'pending' && localParsed.logo_status !== backendParsed.logo_status) {
                      merged.logo_status = backendParsed.logo_status;
                      itemChanged = true;
                    }

                    if (pollCount >= 12 && merged.image_status === 'pending' && !merged.image) {
                      merged.image_status = 'failed';
                      itemChanged = true;
                    }

                    if (itemChanged) {
                      updatedContent = typeof localSec.content === 'string' ? JSON.stringify(merged) : merged;
                    }
                  }

                  return {
                    ...localSec,
                    content: updatedContent,
                  };
                });

                return {
                  ...localPage,
                  sections: updatedSections,
                };
              });
            });

            if (apiRes.globalContactInfo) {
              setGlobalContactInfo(prev => ({ ...prev, ...apiRes.globalContactInfo }));
            }
          }
        } catch (e) {
          console.error("Polling pages failed", e);
        }
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [pages, onPagesChange]);

  // Track live field values for character counting while editing
  const [editValues, setEditValues] = useState<Record<string, Record<string, string>>>({});
  // Track validation errors per section
  const [sectionErrors, setSectionErrors] = useState<Record<string, SectionErrors>>({});
  // Track if user has attempted to save (to show errors on empty fields too)
  const [saveAttemptedSections, setSaveAttemptedSections] = useState<Set<string>>(new Set());
  const [sectionLoading, setSectionLoading] = useState<Record<string, boolean>>({});

  const parseJSONSafely = (raw: any, defaultFallback: any) => {
    if (typeof raw === 'object' && raw !== null) return raw;
    if (typeof raw !== 'string') return defaultFallback;
    try {
      const res = JSON.parse(raw);
      return typeof res === 'object' && res !== null ? res : defaultFallback;
    } catch {
      const fallback = { ...defaultFallback };
      const keys = Object.keys(fallback);
      let foundAny = false;
      for (const key of keys) {
        const regex = new RegExp(`"${key}"\\s*:\\s*"([^"]*)"`);
        const match = raw.match(regex);
        if (match) {
          fallback[key] = match[1];
          foundAny = true;
        }
      }
      if (foundAny) return fallback;

      const mainKey = Object.keys(fallback).find(k => k === 'title' || k === 'name' || k === 'headline' || k === 'text') || 'title';
      fallback[mainKey] = raw;
      return fallback;
    }
  };

  const generateExcerpt = (html: string) => {
    const text = html.replace(/<[^>]*>?/gm, '').trim();
    return text.length > 180 ? text.substring(0, 180) + '...' : text;
  };
  const [pageLoading, setPageLoading] = useState<Record<string, boolean>>({});
  const [regenError, setRegenError] = useState<string | null>(null);

  // Blog post editor modal state (for add/edit/delete individual blog posts)
  const [blogEditor, setBlogEditor] = useState<{
    open: boolean;
    sectionId: string | null;
    postIndex: number | null;
    title: string;
    image: string;
    date: string;
    content: string;
  }>({ open: false, sectionId: null, postIndex: null, title: '', image: '', date: '', content: '' });

  // Add Section modal and drag & drop state
  const [isAddSectionModalOpen, setIsAddSectionModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [sectionSearchQuery, setSectionSearchQuery] = useState<string>('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Custom section block drag-and-drop state
  const [draggedBlock, setDraggedBlock] = useState<{ sectionId: string; blockIdx: number } | null>(null);
  const [dragOverBlock, setDragOverBlock] = useState<{ sectionId: string; blockIdx: number } | null>(null);

  const getCustomBlocksForSection = (sectionId: string, currentVals: Record<string, any>): CustomSectionBlock[] => {
    let blocks: CustomSectionBlock[] = [];
    if (currentVals?.custom_blocks) {
      try {
        blocks = JSON.parse(currentVals.custom_blocks);
      } catch {
        blocks = [];
      }
    }
    if (!Array.isArray(blocks) || blocks.length === 0) {
      const allSections = pages.flatMap(p => p.sections);
      const sec = allSections.find(s => s.id === sectionId) || selectedPage?.sections.find(s => s.id === sectionId);
      if (sec) {
        blocks = normalizeCustomSection(sec.content).blocks;
      }
    }
    if (!Array.isArray(blocks) || blocks.length === 0) {
      blocks = [createDefaultBlock(1)];
    }
    return blocks;
  };

  const setCustomLayout = (sectionId: string, layout: CustomSectionLayout) => {
    let updatedBlocks: CustomSectionBlock[] = [];
    setEditValues(prev => {
      const currentVals = prev[sectionId] || {};
      const blocks = getCustomBlocksForSection(sectionId, currentVals);
      updatedBlocks = blocks;
      return {
        ...prev,
        [sectionId]: {
          ...currentVals,
          custom_layout: layout,
          custom_blocks: JSON.stringify(blocks),
        },
      };
    });
    onPagesChange(prev => prev.map(p => ({
      ...p,
      sections: p.sections.map(s => {
        if (s.id === sectionId) {
          return {
            ...s,
            content: serializeCustomSection(layout, updatedBlocks),
          };
        }
        return s;
      }),
    })));
  };

  const addCustomBlock = (sectionId: string) => {
    let updatedBlocksToSave: CustomSectionBlock[] = [];
    let layoutToSave: CustomSectionLayout = 'story';
    setEditValues(prev => {
      const currentVals = prev[sectionId] || {};
      layoutToSave = (currentVals.custom_layout as CustomSectionLayout) || 'story';
      const blocks = getCustomBlocksForSection(sectionId, currentVals);
      if (blocks.length >= MAX_CUSTOM_BLOCKS) {
        updatedBlocksToSave = blocks;
        return prev;
      }
      const newBlock = createDefaultBlock(blocks.length + 1);
      const updated = [...blocks, newBlock];
      updatedBlocksToSave = updated;
      return {
        ...prev,
        [sectionId]: {
          ...currentVals,
          custom_blocks: JSON.stringify(updated),
        },
      };
    });
    if (updatedBlocksToSave.length > 0) {
      onPagesChange(prev => prev.map(p => ({
        ...p,
        sections: p.sections.map(s => {
          if (s.id === sectionId) {
            return {
              ...s,
              content: serializeCustomSection(layoutToSave, updatedBlocksToSave),
            };
          }
          return s;
        }),
      })));
    }
  };

  const updateCustomBlock = (sectionId: string, blockIndex: number, field: keyof CustomSectionBlock, value: any) => {
    let updatedBlocksToSave: CustomSectionBlock[] = [];
    let layoutToSave: CustomSectionLayout = 'story';
    setEditValues(prev => {
      const currentVals = prev[sectionId] || {};
      layoutToSave = (currentVals.custom_layout as CustomSectionLayout) || 'story';
      const blocks = getCustomBlocksForSection(sectionId, currentVals);
      const safeIndex = Math.max(0, Math.min(blockIndex, blocks.length - 1));
      const updatedBlocks = [...blocks];
      updatedBlocks[safeIndex] = {
        ...updatedBlocks[safeIndex],
        [field]: value,
      };
      updatedBlocksToSave = updatedBlocks;
      return {
        ...prev,
        [sectionId]: {
          ...currentVals,
          custom_blocks: JSON.stringify(updatedBlocks),
        },
      };
    });
    onPagesChange(prev => prev.map(p => ({
      ...p,
      sections: p.sections.map(s => {
        if (s.id === sectionId) {
          return {
            ...s,
            content: serializeCustomSection(layoutToSave, updatedBlocksToSave),
          };
        }
        return s;
      }),
    })));
  };

  const deleteCustomBlock = (sectionId: string, blockIndex: number) => {
    let updatedBlocksToSave: CustomSectionBlock[] = [];
    let layoutToSave: CustomSectionLayout = 'story';
    setEditValues(prev => {
      const currentVals = prev[sectionId] || {};
      layoutToSave = (currentVals.custom_layout as CustomSectionLayout) || 'story';
      const blocks = getCustomBlocksForSection(sectionId, currentVals);
      if (blocks.length <= 1) {
        const resetBlock = createDefaultBlock(1);
        updatedBlocksToSave = [resetBlock];
        return {
          ...prev,
          [sectionId]: {
            ...currentVals,
            custom_blocks: JSON.stringify([resetBlock]),
          },
        };
      }
      const updatedBlocks = blocks.filter((_, idx) => idx !== blockIndex);
      updatedBlocksToSave = updatedBlocks;
      return {
        ...prev,
        [sectionId]: {
          ...currentVals,
          custom_blocks: JSON.stringify(updatedBlocks),
        },
      };
    });
    onPagesChange(prev => prev.map(p => ({
      ...p,
      sections: p.sections.map(s => {
        if (s.id === sectionId) {
          return {
            ...s,
            content: serializeCustomSection(layoutToSave, updatedBlocksToSave),
          };
        }
        return s;
      }),
    })));
  };

  const moveCustomBlock = (sectionId: string, fromIndex: number, toIndex: number) => {
    let updatedBlocksToSave: CustomSectionBlock[] = [];
    let layoutToSave: CustomSectionLayout = 'story';
    setEditValues(prev => {
      const currentVals = prev[sectionId] || {};
      layoutToSave = (currentVals.custom_layout as CustomSectionLayout) || 'story';
      const blocks = getCustomBlocksForSection(sectionId, currentVals);
      if (fromIndex < 0 || fromIndex >= blocks.length || toIndex < 0 || toIndex >= blocks.length) return prev;
      const updatedBlocks = [...blocks];
      const [moved] = updatedBlocks.splice(fromIndex, 1);
      updatedBlocks.splice(toIndex, 0, moved);
      updatedBlocksToSave = updatedBlocks;
      return {
        ...prev,
        [sectionId]: {
          ...currentVals,
          custom_blocks: JSON.stringify(updatedBlocks),
        },
      };
    });
    if (updatedBlocksToSave.length > 0) {
      onPagesChange(prev => prev.map(p => ({
        ...p,
        sections: p.sections.map(s => {
          if (s.id === sectionId) {
            return {
              ...s,
              content: serializeCustomSection(layoutToSave, updatedBlocksToSave),
            };
          }
          return s;
        }),
      })));
    }
  };

  const handleBlockDragStart = (e: React.DragEvent, sectionId: string, blockIdx: number) => {
    e.stopPropagation();
    setDraggedBlock({ sectionId, blockIdx });
    e.dataTransfer.setData('text/plain', `${sectionId}:${blockIdx}`);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleBlockDragOver = (e: React.DragEvent, sectionId: string, blockIdx: number) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (!draggedBlock || draggedBlock.sectionId !== sectionId || draggedBlock.blockIdx === blockIdx) return;
    setDragOverBlock({ sectionId, blockIdx });
  };

  const handleBlockDrop = (e: React.DragEvent, sectionId: string, targetBlockIdx: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedBlock || draggedBlock.sectionId !== sectionId || draggedBlock.blockIdx === targetBlockIdx) {
      setDraggedBlock(null);
      setDragOverBlock(null);
      return;
    }
    moveCustomBlock(sectionId, draggedBlock.blockIdx, targetBlockIdx);
    setDraggedBlock(null);
    setDragOverBlock(null);
  };

  const handleBlockDragEnd = () => {
    setDraggedBlock(null);
    setDragOverBlock(null);
  };

  // Image upload error toast state
  const [uploadError, setUploadError] = useState<string | null>(null);
  const uploadErrorTimer = useRef<NodeJS.Timeout>();

  useEffect(() => {
    return () => {
      if (uploadErrorTimer.current) clearTimeout(uploadErrorTimer.current);
    };
  }, []);

  const validateImageFile = (file: File): boolean => {
    const MAX_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setUploadError(`Image too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max allowed: 5 MB.`);
      if (uploadErrorTimer.current) clearTimeout(uploadErrorTimer.current);
      uploadErrorTimer.current = setTimeout(() => setUploadError(null), 4000);
      return false;
    }
    return true;
  };

  const selectedPage = pages.find(p => p.pageId === selectedPageId);

  const moveSectionUp = (index: number) => {
    if (isExported && onRequestUnlock) {
      onRequestUnlock();
      return;
    }
    // Hero is pinned at index 0 and cannot be moved, and no section can move above Hero (index 0)
    if (index <= 1) return;
    const nextPages = pages.map(page => {
      if (page.pageId !== selectedPageId) return page;
      const newSections = [...page.sections];
      const temp = newSections[index];
      newSections[index] = newSections[index - 1];
      newSections[index - 1] = temp;
      return { ...page, sections: newSections };
    });
    onPagesChange(nextPages);
    if (onManualSave) {
      onManualSave(nextPages, globalContactInfo);
    }
  };

  const moveSectionDown = (index: number) => {
    if (isExported && onRequestUnlock) {
      onRequestUnlock();
      return;
    }
    // Hero is pinned at index 0
    if (index === 0) return;
    const currentSections = selectedPage?.sections || [];
    if (index >= currentSections.length - 1) return;
    const nextPages = pages.map(page => {
      if (page.pageId !== selectedPageId) return page;
      const newSections = [...page.sections];
      const temp = newSections[index];
      newSections[index] = newSections[index + 1];
      newSections[index + 1] = temp;
      return { ...page, sections: newSections };
    });
    onPagesChange(nextPages);
    if (onManualSave) {
      onManualSave(nextPages, globalContactInfo);
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (index === 0) {
      e.preventDefault();
      return;
    }
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === 0) return;
    // Hero is pinned at 0, target must be at least index 1
    const target = Math.max(1, index);
    if (dragOverIndex !== target) {
      setDragOverIndex(target);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === 0) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }
    const finalTarget = Math.max(1, targetIndex);
    if (draggedIndex === finalTarget) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const nextPages = pages.map(page => {
      if (page.pageId !== selectedPageId) return page;
      const newSections = [...page.sections];
      const [moved] = newSections.splice(draggedIndex, 1);
      newSections.splice(finalTarget, 0, moved);
      return { ...page, sections: newSections };
    });
    onPagesChange(nextPages);
    if (onManualSave) {
      onManualSave(nextPages, globalContactInfo);
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const deleteSection = (sectionId: string) => {
    if (isExported && onRequestUnlock) {
      onRequestUnlock();
      return;
    }
    const section = selectedPage?.sections.find(s => s.id === sectionId);
    if (section?.type === 'hero') {
      alert('The Hero section is required at the top of the page and cannot be deleted.');
      return;
    }

    if (confirm(`Are you sure you want to delete the "${section?.title || SECTION_DISPLAY_NAMES[section?.type || ''] || 'selected'}" section?`)) {
      if (editingSection === sectionId) {
        setEditingSection(null);
      }
      setEditValues(prev => {
        const next = { ...prev };
        delete next[sectionId];
        return next;
      });
      setSectionErrors(prev => {
        const next = { ...prev };
        delete next[sectionId];
        return next;
      });

      const nextPages = pages.map(page => {
        if (page.pageId !== selectedPageId) return page;
        return {
          ...page,
          sections: page.sections.filter(s => s.id !== sectionId)
        };
      });

      onPagesChange(nextPages);
      if (onManualSave) {
        onManualSave(nextPages, globalContactInfo);
      }
    }
  };

  const handleAddSection = (template: SectionTemplate) => {
    if (isExported && onRequestUnlock) {
      onRequestUnlock();
      return;
    }
    if (template.type === 'hero' && selectedPage?.sections.some(s => s.type === 'hero')) {
      alert('Each page can only contain 1 Hero section at the top.');
      return;
    }

    const newId = `${template.type}-${Date.now()}`;
    const newSection: ContentSection = {
      id: newId,
      title: template.defaultTitle,
      subtitle: template.defaultSubtitle || '',
      type: template.type as any,
      content: template.defaultContent,
      editing: false,
      enabled: true
    };

    const nextPages = pages.map(page => {
      if (page.pageId !== selectedPageId) return page;
      return {
        ...page,
        sections: [...page.sections, newSection]
      };
    });

    onPagesChange(nextPages);
    if (onManualSave) {
      onManualSave(nextPages, globalContactInfo);
    }

    setIsAddSectionModalOpen(false);

    if (template.type === 'custom') {
      const initialPayload = normalizeCustomSection(template.defaultContent);
      setEditValues(prev => ({
        ...prev,
        [newId]: {
          section_title: template.defaultTitle,
          section_subtitle: template.defaultSubtitle || '',
          custom_layout: initialPayload.layout,
          custom_blocks: JSON.stringify(initialPayload.blocks),
        }
      }));
      setEditingSection(newId);
    }
  };

  const toggleSectionEnabled = (sectionId: string) => {
    onPagesChange(prev => prev.map(page => {
      if (page.pageId !== selectedPageId) return page;
      return {
        ...page,
        sections: page.sections.map(section =>
          section.id === sectionId
            ? { ...section, enabled: section.enabled !== false ? false : true }
            : section
        ),
      };
    }));
  };

  // Get blog posts from the Blog page for homepage display
  const blogPage = pages.find(p => p.pageId === 'blog');
  const blogPagePosts = blogPage?.sections.find(s => s.type === 'blog');
  const blogPostsCount = Array.isArray(blogPagePosts?.content) ? blogPagePosts.content.length : 0;
  const hasEnoughBlogs = blogPostsCount >= 3;

  // Get gallery items from the Gallery page for homepage display
  const galleryPage = pages.find(p => p.pageId === 'gallery');
  const galleryPageItemsSection = galleryPage?.sections.find(s => s.type === 'gallery');

  // Get contact form from the Contact Us page for homepage display
  const contactPage = pages.find(p => p.pageId === 'contact');
  const contactPageForm = contactPage?.sections.find(s => s.type === 'contact');
  const hasContactForm = !!contactPageForm;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Generated':
        return 'bg-[#e8f9fb] text-[#0b7f8f] border border-[#cdeff3]';
      case 'Edited':
        return 'bg-[#fdf8e8] text-[#b7791f] border border-[#f6e7b0]';
      case 'Pending':
        return 'bg-slate-50 text-slate-600 border border-slate-200';
      default:
        return 'bg-slate-50 text-slate-600 border border-slate-200';
    }
  };

  const toggleEdit = (sectionId: string) => {
    if (isExported && onRequestUnlock) {
      onRequestUnlock();
      return;
    }
    if (editingSection === sectionId) {
      let vals = { ...(editValues?.[sectionId] || {}) };
      const section = selectedPage?.sections.find(s => s.id === sectionId);
      if (section && (section.type === 'contact' || section.type === 'contactInfo')) {
        vals.email = globalContactInfo.email;
        vals.phone = globalContactInfo.phone;
        vals.location = globalContactInfo.location;
        vals.description = globalContactInfo.description;
      }
      if (section) {
        // Check hero via window hook
        if (section.type === 'hero') {
          const heroValid = (window as Record<string, unknown>).__heroValidate?.() as boolean;
          if (!heroValid) {
            setSaveAttemptedSections(prev => new Set(prev).add(sectionId));
            return; // stay in edit mode
          }
        }
        const errors = validateSection(section, vals);
        if (Object.keys(errors).length > 0) {
          setSectionErrors(prev => ({ ...prev, [sectionId]: errors }));
          setSaveAttemptedSections(prev => new Set(prev).add(sectionId));
          return; // stay in edit mode
        }
      }

      // Valid — persist
      setSectionErrors(prev => {
        const next = { ...prev };
        delete next[sectionId];
        return next;
      });
      setSaveAttemptedSections(prev => {
        const next = new Set(prev);
        next.delete(sectionId);
        return next;
      });

      onPagesChange(prev => prev.map(page => {
        if (page.pageId !== selectedPageId) return page;
        return {
          ...page,
          sections: page.sections.map(section => {
            if (section.id !== sectionId) return section;
            const vals = editValues[sectionId] || {};

            const newTitle = vals.section_title !== undefined ? vals.section_title : section.title;
            const newSubtitle = vals.section_subtitle !== undefined ? vals.section_subtitle : (section.subtitle ?? '');
            const newDesc = vals.section_subtitle !== undefined ? vals.section_subtitle : (section.description ?? '');

            let updatedSection: ContentSection = {
              ...section,
              title: newTitle,
              subtitle: newSubtitle,
              description: newDesc,
            };

            if (section.type === 'story') {
              return { ...updatedSection, content: vals.text ?? section.content };
            }
            if (section.type === 'usp' || section.type === 'grid' || section.type === 'blog') {
              const arr = Array.isArray(section.content) ? [...section.content] : [];
              const next = arr.map((item, idx) => {
                if (section.type === 'usp' || section.type === 'grid') {
                  const parsed = parseJSONSafely(item, { title: '', description: '', icon: '' });
                  const prefix = section.type === 'usp' ? 'usp_' : '';
                  return JSON.stringify({
                    ...parsed,
                    title: vals[`${prefix}${idx}_title`] ?? parsed.title,
                    description: vals[`${prefix}${idx}_desc`] ?? parsed.description,
                    icon: vals[`${prefix}${idx}_icon`] ?? parsed.icon,
                  });
                }
                if (section.type === 'blog') {
                  const parsed = parseJSONSafely(item, { title: '', excerpt: '', content: '', date: '' });
                  return JSON.stringify({
                    ...parsed,
                    title: vals[`${idx}_title`] ?? parsed.title,
                    excerpt: vals[`${idx}_excerpt`] ?? parsed.excerpt,
                    content: vals[`${idx}_content`] ?? parsed.content,
                    date: vals[`${idx}_date`] ?? parsed.date,
                  });
                }
                return item;
              });
              return { ...updatedSection, content: next };
            }
            if (section.type === 'founder') {
              const arr = Array.isArray(section.content) && section.content.length > 0 ? [...section.content] : [JSON.stringify({ name: '', role: '', bio: '', image: '' })];
              const next = arr.map((item) => {
                const parsed = parseJSONSafely(item, { name: '', role: '', bio: '', image: '' });
                return JSON.stringify({
                  ...parsed,
                  name: vals['0_name'] ?? parsed.name,
                  role: vals['0_role'] ?? parsed.role,
                  bio: vals['0_bio'] ?? parsed.bio,
                  image: vals['0_image'] ?? parsed.image,
                });
              });
              return { ...updatedSection, content: next };
            }
            if (section.type === 'clientLogos') {
              const arr = Array.isArray(section.content) ? [...section.content] : [];
              const next = arr.map((item, idx) => {
                const parsed = parseJSONSafely(item, { name: '', logo: '' });
                return JSON.stringify({
                  ...parsed,
                  name: vals[`client_${idx}_name`] ?? parsed.name,
                  logo: vals[`client_${idx}_logo`] ?? parsed.logo,
                });
              });
              return { ...updatedSection, content: next };
            }
            if (section.type === 'team') {
              const arr = Array.isArray(section.content) ? [...section.content] : [];
              const next = arr.map((item, idx) => {
                const parsed = parseJSONSafely(item, { name: '', role: '', bio: '', image: '' });
                return JSON.stringify({
                  ...parsed,
                  name: vals[`${idx}_name`] ?? parsed.name,
                  role: vals[`${idx}_role`] ?? parsed.role,
                  bio: vals[`${idx}_bio`] ?? parsed.bio,
                  image: vals[`${idx}_image`] ?? parsed.image,
                });
              });
              return { ...updatedSection, content: next };
            }
            if (section.type === 'contact' || section.type === 'contactInfo') {
              // Update global state immediately when saving
              setGlobalContactInfo({
                email: vals.email ?? '',
                phone: vals.phone ?? '',
                location: vals.location ?? '',
                description: vals.description ?? '',
                socials: globalContactInfo.socials
              });
              return { 
                ...updatedSection, 
                content: JSON.stringify({ description: vals.description ?? '' }) // no duplicate fields
              };
            }
            if (section.type === 'contactForm') {
              return { 
                ...updatedSection, 
                content: JSON.stringify({
                  heading: vals.heading ?? vals.section_title ?? 'Send a Message',
                  subtext: vals.subtext ?? vals.section_subtitle ?? ''
                }) 
              };
            }
            if (section.type === 'problemSolution') {
              const arr = Array.isArray(section.content) ? [...section.content] : [];
              const next = arr.map((item) => {
                const parsed = parseJSONSafely(item, { headline: '', problems: [] });
                const problems = (parsed.problems ?? []).map((p: { pain: string; solution: string }, idx: number) => ({
                  pain: vals[`ps_pain_${idx}`] ?? p.pain,
                  solution: vals[`ps_sol_${idx}`] ?? p.solution,
                }));
                return JSON.stringify({
                  ...parsed,
                  headline: vals['ps_headline'] ?? vals['section_title'] ?? parsed.headline,
                  problems,
                });
              });
              return { ...updatedSection, content: next };
            }
            if (section.type === 'serviceCard' || section.type === 'productCard') {
              const arr = Array.isArray(section.content) ? [...section.content] : [];
              const next = arr.map((item, idx) => {
                const parsed = parseJSONSafely(item, { name: '', description: '', url: '', icon: 'ri-briefcase-line', image: '' });
                return JSON.stringify({
                  ...parsed,
                  name: vals[`card_${idx}_name`] ?? parsed.name,
                  description: vals[`card_${idx}_desc`] ?? parsed.description,
                  url: vals[`card_${idx}_url`] ?? parsed.url,
                  icon: vals[`card_${idx}_icon`] ?? parsed.icon,
                  image: vals[`card_${idx}_image`] ?? parsed.image,
                });
              });
              return { ...updatedSection, content: next };
            }
            if (section.type === 'testimonial') {
              const arr = Array.isArray(section.content) ? [...section.content] : [];
              const next = arr.map((item, idx) => {
                const parsed = parseJSONSafely(item, { quote: '', client: '', role: '' });
                return JSON.stringify({
                  ...parsed,
                  quote: vals[`test_${idx}_quote`] ?? parsed.quote,
                  client: vals[`test_${idx}_client`] ?? parsed.client,
                  role: vals[`test_${idx}_role`] ?? parsed.role,
                });
              });
              return { ...updatedSection, content: next };
            }
            if (section.type === 'faq') {
              const arr = Array.isArray(section.content) ? [...section.content] : [];
              const next = arr.map((item, idx) => {
                const parsed = parseJSONSafely(item, { question: '', answer: '' });
                return JSON.stringify({
                  ...parsed,
                  question: vals[`faq_${idx}_q`] ?? parsed.question,
                  answer: vals[`faq_${idx}_a`] ?? parsed.answer,
                });
              });
              return { ...updatedSection, content: next };
            }
            if (section.type === 'custom' || section.type === 'customSection') {
              const layout = (vals.custom_layout as CustomSectionLayout) || 'story';
              let blocks: CustomSectionBlock[] = [];
              try {
                blocks = JSON.parse(vals.custom_blocks || '[]');
              } catch {
                blocks = [];
              }
              if (!blocks || blocks.length === 0) {
                blocks = [createDefaultBlock(1)];
              }
              const updatedContent = serializeCustomSection(layout, blocks);
              return { ...updatedSection, content: updatedContent };
            }
            return updatedSection;
          }),
        };
      }));
      setEditValues(prev => {
        const next = { ...prev };
        delete next[sectionId];
        return next;
      });
      setEditingSection(null);
    } else {
      // Start editing — load current values into editValues
      const section = pages.flatMap(p => p.sections).find(s => s.id === sectionId) || selectedPage?.sections.find(s => s.id === sectionId);
      if (section) {
        const vals: Record<string, string> = {};
        vals.section_title = section.title ?? SECTION_DISPLAY_NAMES[section.type] ?? '';
        vals.section_subtitle = section.subtitle ?? section.description ?? '';
        if (section.type === 'story') {
          let extracted = '';
          if (typeof section.content === 'string') extracted = section.content;
          else if (Array.isArray(section.content) && section.content.length > 0) {
            const first = section.content[0];
            if (typeof first === 'string') extracted = first;
            else if (typeof first === 'object' && first !== null) extracted = first.text || first.story || '';
          }
          else if (typeof section.content === 'object' && section.content !== null) {
            // @ts-ignore
            extracted = section.content.text || section.content.story || section.content.content || '';
          }
          vals.text = extracted;
        }
        if (section.type === 'usp' || section.type === 'grid' || section.type === 'blog') {
          const arr = Array.isArray(section.content) ? section.content : [];
          arr.forEach((item, idx) => {
            if (section.type === 'usp' || section.type === 'grid') {
              const parsed = parseJSONSafely(item, { title: '', description: '', icon: '' });
              vals[`${idx}_title`] = parsed.title ?? '';
              vals[`${idx}_desc`] = parsed.description ?? '';
            }
            if (section.type === 'blog') {
              const parsed = parseJSONSafely(item, { title: '', excerpt: '', content: '', date: '' });
              vals[`${idx}_title`] = parsed.title ?? '';
              vals[`${idx}_excerpt`] = parsed.excerpt ?? '';
              vals[`${idx}_content`] = parsed.content ?? '';
              vals[`${idx}_date`] = parsed.date ?? '';
            }
          });
        }
        if (section.type === 'founder') {
          const arr = Array.isArray(section.content) ? section.content : (section.content ? [section.content] : []);
          if (arr.length > 0) {
            const parsed = parseJSONSafely(arr[0], { name: '', role: '', bio: '', image: '' });
            vals['0_name'] = parsed.name ?? '';
            vals['0_role'] = parsed.role ?? '';
            vals['0_bio'] = parsed.bio ?? '';
            vals['0_image'] = parsed.image ?? '';
          }
        }
        if (section.type === 'clientLogos') {
          const arr = Array.isArray(section.content) ? section.content : [];
          arr.forEach((item, idx) => {
            const parsed = parseJSONSafely(item, { name: '', logo: '' });
            vals[`client_${idx}_name`] = parsed.name ?? '';
            vals[`client_${idx}_logo`] = parsed.logo ?? '';
          });
        }
        if (section.type === 'team') {
          const arr = Array.isArray(section.content) ? section.content : [];
          arr.forEach((item, idx) => {
            const parsed = parseJSONSafely(item, { name: '', role: '', bio: '', image: '' });
            vals[`${idx}_name`] = parsed.name ?? '';
            vals[`${idx}_role`] = parsed.role ?? '';
            vals[`${idx}_bio`] = parsed.bio ?? '';
            vals[`${idx}_image`] = parsed.image ?? '';
          });
        }
        if (section.type === 'contact' || section.type === 'contactInfo') {
          try {
            const parsed = JSON.parse(section.content as string);
            vals.description = parsed.description ?? '';
            vals.email = parsed.email ?? '';
            vals.phone = parsed.phone ?? '';
            vals.location = parsed.location ?? '';
          } catch {
            vals.description = typeof section.content === 'string' ? section.content : '';
            vals.email = globalContactInfo.email || 'Enter your email';
            vals.phone = globalContactInfo.phone || '----------';
            vals.location = globalContactInfo.location || 'Add your location';
          }
        }
        if (section.type === 'problemSolution') {
          const arr = Array.isArray(section.content) ? section.content : [];
          if (arr.length > 0) {
            const parsed = parseJSONSafely(arr[0], { headline: '', problems: [] });
            vals['ps_headline'] = parsed.headline ?? '';
            (parsed.problems ?? []).forEach((p: { pain: string; solution: string }, idx: number) => {
              vals[`ps_pain_${idx}`] = p.pain ?? '';
              vals[`ps_sol_${idx}`] = p.solution ?? '';
            });
          }
        }
        if (section.type === 'serviceCard' || section.type === 'productCard') {
          const arr = Array.isArray(section.content) ? section.content : [];
          arr.forEach((item, idx) => {
            const parsed = parseJSONSafely(item, { name: '', description: '', url: '', icon: 'ri-briefcase-line', image: '' });
            vals[`card_${idx}_name`] = parsed.name ?? '';
            vals[`card_${idx}_desc`] = parsed.description ?? '';
            vals[`card_${idx}_url`] = parsed.url ?? '';
            vals[`card_${idx}_icon`] = parsed.icon ?? 'ri-briefcase-line';
            vals[`card_${idx}_image`] = parsed.image ?? '';
          });
        }
        if (section.type === 'testimonial') {
          const arr = Array.isArray(section.content) ? section.content : [];
          arr.forEach((item, idx) => {
            const parsed = parseJSONSafely(item, { quote: '', client: '', role: '' });
            vals[`test_${idx}_quote`] = parsed.quote ?? '';
            vals[`test_${idx}_client`] = parsed.client ?? '';
            vals[`test_${idx}_role`] = parsed.role ?? '';
          });
        }
        if (section.type === 'faq') {
          const arr = Array.isArray(section.content) ? section.content : [];
          arr.forEach((item, idx) => {
            const parsed = parseJSONSafely(item, { question: '', answer: '' });
            vals[`faq_${idx}_q`] = parsed.question ?? '';
            vals[`faq_${idx}_a`] = parsed.answer ?? '';
          });
        }
        if (section.type === 'custom' || section.type === 'customSection') {
          const payload = normalizeCustomSection(section.content);
          vals.custom_layout = payload.layout;
          vals.custom_blocks = JSON.stringify(payload.blocks);
        }
        setEditValues(prev => ({ ...prev, [sectionId]: vals }));
      }
      setEditingSection(sectionId);
    }
  };

  const updateEditValue = (sectionId: string, key: string, value: string, max?: number) => {
    if (max !== undefined && max > 0 && !key.endsWith('_image') && !key.endsWith('_logo') && !key.startsWith('client_') && !value.startsWith('data:') && value.length > max) return;
    setEditValues(prev => ({
      ...prev,
      [sectionId]: { ...(prev[sectionId] || {}), [key]: value },
    }));
    // Clear error for this field if exists
    setSectionErrors(prev => {
      const sectionErrs = prev[sectionId];
      if (!sectionErrs?.[key]) return prev;
      const next = { ...prev, [sectionId]: { ...sectionErrs } };
      delete next[sectionId][key];
      if (Object.keys(next[sectionId]).length === 0) delete next[sectionId];
      return next;
    });
  };

  const updateSectionContent = (sectionId: string, newContent: string[] | string) => {
    onPagesChange(prev =>
      prev.map(page =>
        page.pageId === selectedPageId
          ? {
              ...page,
              sections: page.sections.map(section =>
                section.id === sectionId
                  ? { ...section, content: newContent }
                  : section
              ),
            }
          : page
      )
    );
  };

  const addGridCard = (sectionId: string) => {
    onPagesChange(prev =>
      prev.map(page =>
        page.pageId === selectedPageId
          ? {
              ...page,
              sections: page.sections.map(section => {
                if (section.id !== sectionId || section.type !== 'grid') return section;
                const cards = Array.isArray(section.content) ? [...section.content] : [];
                const newCard = JSON.stringify({
                  title: 'New Item',
                  description: 'Describe this item here.',
                  icon: 'ri-briefcase-line',
                });
                return { ...section, content: [...cards, newCard] };
              }),
            }
          : page
      )
    );
  };

  const deleteGridCard = (sectionId: string, cardIndex: number) => {
    onPagesChange(prev =>
      prev.map(page =>
        page.pageId === selectedPageId
          ? {
              ...page,
              sections: page.sections.map(section => {
                if (section.id !== sectionId || section.type !== 'grid') return section;
                const cards = Array.isArray(section.content) ? [...section.content] : [];
                cards.splice(cardIndex, 1);
                return { ...section, content: cards };
              }),
            }
          : page
      )
    );
  };

  const addTeamMember = (sectionId: string) => {
    onPagesChange(prev =>
      prev.map(page =>
        page.pageId === selectedPageId
          ? {
              ...page,
              sections: page.sections.map(section => {
                if (section.id !== sectionId || section.type !== 'team') return section;
                const members = Array.isArray(section.content) ? [...section.content] : [];
                const newMember = JSON.stringify({
                  name: 'New Team Member',
                  role: 'Role Title',
                  bio: 'Short bio describing their expertise and background.',
                  image: '',
                });
                return { ...section, content: [...members, newMember] };
              }),
            }
          : page
      )
    );
  };

  const deleteTeamMember = (sectionId: string, memberIndex: number) => {
    onPagesChange(prev =>
      prev.map(page =>
        page.pageId === selectedPageId
          ? {
              ...page,
              sections: page.sections.map(section => {
                if (section.id !== sectionId || section.type !== 'team') return section;
                const members = Array.isArray(section.content) ? [...section.content] : [];
                members.splice(memberIndex, 1);
                return { ...section, content: members };
              }),
            }
          : page
      )
    );
  };

  const handlePersonImageUpload = (sectionId: string, index: number, dataUrl: string) => {
    onPagesChange(prev =>
      prev.map(page =>
        page.pageId === selectedPageId
            ? {
                ...page,
                sections: page.sections.map(section => {
                  if (section.id !== sectionId) return section;
                  const arr = Array.isArray(section.content) && section.content.length > 0 ? [...section.content] : [JSON.stringify({ name: '', role: '', bio: '', image: '' })];
                  const next = arr.map((item, idx) => {
                    if (idx !== index) return item;
                    const parsed = parseJSONSafely(item, { name: '', role: '', bio: '', image: '' });
                    return JSON.stringify({ ...parsed, image: dataUrl });
                  });
                  return { ...section, content: next };
                }),
              }
            : page
        )
      );
  };

  // --- Blog post management ---
  const addBlogPost = (sectionId: string) => {
    onPagesChange(prev =>
      prev.map(page =>
        page.pageId === selectedPageId
          ? {
              ...page,
              sections: page.sections.map(section => {
                if (section.id !== sectionId || section.type !== 'blog') return section;
                const posts = Array.isArray(section.content) ? [...section.content] : [];
                const newPost = JSON.stringify({
                  title: 'New Blog Post',
                  image: '',
                  date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
                  content: 'Write your full blog content here. Aim for at least 100 characters to provide value to your readers. This is where you share insights, tips, and thought leadership that positions your brand as an authority in your space.',
                });
                return { ...section, content: [...posts, newPost] };
              }),
            }
          : page
      )
    );
  };

  const deleteBlogPost = (sectionId: string, postIndex: number) => {
    onPagesChange(prev =>
      prev.map(page =>
        page.pageId === selectedPageId
          ? {
              ...page,
              sections: page.sections.map(section => {
                if (section.id !== sectionId || section.type !== 'blog') return section;
                const posts = Array.isArray(section.content) ? [...section.content] : [];
                posts.splice(postIndex, 1);
                return { ...section, content: posts };
              }),
            }
          : page
      )
    );
  };

  const openBlogEditor = (sectionId: string, postIndex: number) => {
    const section = selectedPage?.sections.find(s => s.id === sectionId);
    if (!section) return;
    const posts = Array.isArray(section.content) ? section.content : [];
    const post = posts[postIndex];
    if (!post) return;
    let parsed;
    try {
      parsed = JSON.parse(post);
    } catch {
      parsed = { title: '', image: '', date: '', content: '' };
    }
    setBlogEditor({
      open: true,
      sectionId,
      postIndex,
      title: parsed.title ?? '',
      image: parsed.image ?? '',
      date: parsed.date ?? '',
      content: parsed.content ?? '',
    });
  };

  const saveBlogEditor = () => {
    if (!blogEditor.sectionId || blogEditor.postIndex === null) return;
    onPagesChange(prev =>
      prev.map(page =>
        page.pageId === selectedPageId
          ? {
              ...page,
              sections: page.sections.map(section => {
                if (section.id !== blogEditor.sectionId || section.type !== 'blog') return section;
                const posts = Array.isArray(section.content) ? [...section.content] : [];
                const prevPost = parseJSONSafely(posts[blogEditor.postIndex!], {});
                posts[blogEditor.postIndex!] = JSON.stringify({
                  ...prevPost,
                  title: blogEditor.title,
                  image: blogEditor.image,
                  date: blogEditor.date,
                  content: blogEditor.content,
                  image_status: blogEditor.image ? 'completed' : prevPost.image_status,
                });
                return { ...section, content: posts };
              }),
            }
          : page
      )
    );
    setBlogEditor({ open: false, sectionId: null, postIndex: null, title: '', image: '', date: '', content: '' });
  };

  // --- ServiceCard / ProductCard management ---
  const addUspItem = (sectionId: string) => {
    onPagesChange(prev =>
      prev.map(page =>
        page.pageId === selectedPageId
          ? {
              ...page,
              sections: page.sections.map(section => {
                if (section.id !== sectionId || (section.type !== 'usp' && section.type !== 'grid')) return section;
                const items = Array.isArray(section.content) ? [...section.content] : [];
                const newItem = JSON.stringify({
                  title: '',
                  description: '',
                  icon: 'ri-checkbox-circle-line',
                });
                return { ...section, content: [...items, newItem] };
              }),
            }
          : page
      )
    );
  };

  const deleteUspItem = (sectionId: string, idx: number) => {
    onPagesChange(prev =>
      prev.map(page =>
        page.pageId === selectedPageId
          ? {
              ...page,
              sections: page.sections.map(section => {
                if (section.id !== sectionId || (section.type !== 'usp' && section.type !== 'grid')) return section;
                const items = Array.isArray(section.content) ? [...section.content] : [];
                items.splice(idx, 1);
                return { ...section, content: items };
              }),
            }
          : page
      )
    );
  };

  const addServiceItem = (sectionId: string) => {
    onPagesChange(prev =>
      prev.map(page =>
        page.pageId === selectedPageId
          ? {
              ...page,
              sections: page.sections.map(section => {
                if (section.id !== sectionId || (section.type !== 'serviceCard' && section.type !== 'productCard')) return section;
                const cards = Array.isArray(section.content) ? [...section.content] : [];
                const newCard = JSON.stringify({
                  name: '',
                  description: '',
                  url: '',
                  icon: 'ri-briefcase-line',
                  image: '',
                });
                return { ...section, content: [...cards, newCard] };
              }),
            }
          : page
      )
    );
  };

  const deleteServiceItem = (sectionId: string, idx: number) => {
    onPagesChange(prev =>
      prev.map(page =>
        page.pageId === selectedPageId
          ? {
              ...page,
              sections: page.sections.map(section => {
                if (section.id !== sectionId || (section.type !== 'serviceCard' && section.type !== 'productCard')) return section;
                const cards = Array.isArray(section.content) ? [...section.content] : [];
                cards.splice(idx, 1);
                return { ...section, content: cards };
              }),
            }
          : page
      )
    );
  };

  // --- Testimonial management ---
  const addTestimonial = (sectionId: string) => {
    onPagesChange(prev =>
      prev.map(page =>
        page.pageId === selectedPageId
          ? {
              ...page,
              sections: page.sections.map(section => {
                if (section.id !== sectionId || section.type !== 'testimonial') return section;
                const quotes = Array.isArray(section.content) ? [...section.content] : [];
                const newQuote = JSON.stringify({
                  quote: '',
                  client: '',
                  role: '',
                });
                return { ...section, content: [...quotes, newQuote] };
              }),
            }
          : page
      )
    );
  };

  const deleteTestimonial = (sectionId: string, idx: number) => {
    onPagesChange(prev =>
      prev.map(page =>
        page.pageId === selectedPageId
          ? {
              ...page,
              sections: page.sections.map(section => {
                if (section.id !== sectionId || section.type !== 'testimonial') return section;
                const quotes = Array.isArray(section.content) ? [...section.content] : [];
                quotes.splice(idx, 1);
                return { ...section, content: quotes };
              }),
            }
          : page
      )
    );
  };
  
  // --- Client Logo management ---
  const addClientLogo = (sectionId: string) => {
    onPagesChange(prev =>
      prev.map(page =>
        page.pageId === selectedPageId
          ? {
              ...page,
              sections: page.sections.map(section => {
                if (section.id !== sectionId || section.type !== 'clientLogos') return section;
                const logos = Array.isArray(section.content) ? [...section.content] : [];
                const newLogo = JSON.stringify({
                  name: '',
                  logo: '',
                });
                return { ...section, content: [...logos, newLogo] };
              }),
            }
          : page
      )
    );
  };

  const deleteClientLogo = (sectionId: string, idx: number) => {
    onPagesChange(prev =>
      prev.map(page =>
        page.pageId === selectedPageId
          ? {
              ...page,
              sections: page.sections.map(section => {
                if (section.id !== sectionId || section.type !== 'clientLogos') return section;
                const logos = Array.isArray(section.content) ? [...section.content] : [];
                logos.splice(idx, 1);
                return { ...section, content: logos };
              }),
            }
          : page
      )
    );
  };

  // --- FAQ management ---
  const addFaq = (sectionId: string) => {
    onPagesChange(prev =>
      prev.map(page =>
        page.pageId === selectedPageId
          ? {
              ...page,
              sections: page.sections.map(section => {
                if (section.id !== sectionId || section.type !== 'faq') return section;
                const faqs = Array.isArray(section.content) ? [...section.content] : [];
                const newFaq = JSON.stringify({
                  question: '',
                  answer: '',
                });
                return { ...section, content: [...faqs, newFaq] };
              }),
            }
          : page
      )
    );
  };

  const deleteFaq = (sectionId: string, idx: number) => {
    onPagesChange(prev =>
      prev.map(page =>
        page.pageId === selectedPageId
          ? {
              ...page,
              sections: page.sections.map(section => {
                if (section.id !== sectionId || section.type !== 'faq') return section;
                const faqs = Array.isArray(section.content) ? [...section.content] : [];
                faqs.splice(idx, 1);
                return { ...section, content: faqs };
              }),
            }
          : page
      )
    );
  };

  const handleImageUpload = (sectionId: string, logoKey: string, dataUrl: string) => {
    updateEditValue(sectionId, logoKey, dataUrl, 1000000); // Higher limit for Base64
  };

  const handleGeneratePage = async (pageId: string) => {
    if (isExported && onRequestUnlock) {
      onRequestUnlock();
      return;
    }

    // Validate mandatory Section Heading for custom sections before generating
    const targetPage = pages.find(p => p.pageId === pageId);
    if (targetPage) {
      const invalidCustomSection = targetPage.sections.find(s => {
        if (s.type === 'custom' || s.type === 'customSection') {
          const heading = (editValues[s.id]?.section_title ?? s.title ?? '').trim();
          return !heading;
        }
        return false;
      });

      if (invalidCustomSection) {
        setRegenError('Please provide a Section Heading for all Custom Sections before generating page content.');
        setEditingSection(invalidCustomSection.id);
        setSectionErrors(prev => ({
          ...prev,
          [invalidCustomSection.id]: {
            section_title: { message: 'Section Heading is required (* Compulsory)' }
          }
        }));
        setTimeout(() => setRegenError(null), 6000);
        return;
      }
    }

    setPageLoading(prev => ({ ...prev, [pageId]: true }));
    try {
      // Save any local edits across all pages before generating, so they aren't lost on refetch
      await api.saveContent(pages, globalContactInfo);

      const genRes = await api.generateWebsiteContent(pageId);
      if (genRes && genRes.status === 'error') {
        throw new Error(genRes.message || 'Page generation failed');
      }

      // Refetch the pages to get the updated content from DB
      const apiRes = await api.getPages();
      if (apiRes && Array.isArray(apiRes.pages) && apiRes.pages.length > 0) {
        originalOnPagesChange(deduplicatePages(apiRes.pages));
      }
      if (apiRes && apiRes.globalContactInfo) {
        setGlobalContactInfo(prev => ({ ...prev, ...apiRes.globalContactInfo }));
      }
    } catch (err) {
      console.error("Page generation failed:", err);
    } finally {
      setPageLoading(prev => ({ ...prev, [pageId]: false }));
    }
  };

  const handleRegenerate = async (sectionId: string) => {
    if (isExported && onRequestUnlock) {
      onRequestUnlock();
      return;
    }
    const section = selectedPage?.sections.find(s => s.id === sectionId);
    if (!section) return;

    if (section.type === 'custom' || section.type === 'customSection') {
      const heading = (editValues[sectionId]?.section_title ?? section.title ?? '').trim();
      if (!heading) {
        setRegenError('Please provide a Section Heading before generating this custom section.');
        setEditingSection(sectionId);
        setSectionErrors(prev => ({
          ...prev,
          [sectionId]: {
            section_title: { message: 'Section Heading is required (* Compulsory)' }
          }
        }));
        setTimeout(() => setRegenError(null), 6000);
        return;
      }
    }

    setSectionLoading(prev => ({ ...prev, [sectionId]: true }));
    setRegenError(null);

    try {
      // Save any local edits across all pages before generating, so they aren't lost
      await api.saveContent(pages, globalContactInfo);

      const json = await api.regenerateWebsiteSection(selectedPageId!, section.id, section.type);
      if (json.data && json.data.content) {
        if (section.type === 'contact' || section.type === 'contactInfo') {
          try {
            const parsed = JSON.parse(json.data.content);
            const newDesc = parsed.description || parsed.content || '';
            setGlobalContactInfo(prev => ({ ...prev, description: newDesc }));
            // Only save the description in the section content to prevent AI from overwriting email/phone
            json.data.content = JSON.stringify({ description: newDesc });
          } catch (e) {
            setGlobalContactInfo(prev => ({ ...prev, description: json.data.content }));
          }
        }

        // Update state
        onPagesChange(prev =>
          prev.map(page =>
            page.pageId === selectedPageId
              ? { ...page, sections: page.sections.map(sec => sec.id === sectionId ? { ...sec, content: json.data.content } : sec) }
              : page
          )
        );
      }
    } catch (err) {
      console.error("Regeneration failed:", err);
      setRegenError("Failed to regenerate section. Your previous content was preserved.");
      setTimeout(() => setRegenError(null), 5000);
    } finally {
      setSectionLoading(prev => ({ ...prev, [sectionId]: false }));
    }
  };

  const handleRegenerateGlobalFooter = async () => {
    if (isExported && onRequestUnlock) {
      onRequestUnlock();
      return;
    }
    setSectionLoading(prev => ({ ...prev, 'global-footer': true }));
    setRegenError(null);
    try {
      // Save any local edits across all pages before generating, so they aren't lost
      await api.saveContent(pages, globalContactInfo);

      // Use the existing contactInfo prompt endpoint which provides high-quality results without hallucinating
      const json = await api.regenerateWebsiteSection('contact', 'contactInfo', 'contactInfo');
      if (json.data && json.data.content) {
        try {
          const parsed = JSON.parse(json.data.content);
          const newDesc = parsed.description || parsed.content || json.data.content;
          setGlobalContactInfo(prev => ({ ...prev, description: newDesc }));
        } catch {
          setGlobalContactInfo(prev => ({ ...prev, description: json.data.content }));
        }
      }
    } catch (err) {
      console.error("Regeneration failed:", err);
      setRegenError("Failed to regenerate footer description.");
      setTimeout(() => setRegenError(null), 5000);
    } finally {
      setSectionLoading(prev => ({ ...prev, 'global-footer': false }));
    }
  };

  const getInputClass = (sectionId: string, key: string, base: string) => {
    const hasError = !!sectionErrors[sectionId]?.[key];
    return `${base} ${hasError ? 'border-red-400 focus:border-red-400 bg-red-50/30' : 'border-gray-300 focus:border-gold'}`;
  };

  const renderSectionContent = (originalSection: ContentSection) => {
    let section = { ...originalSection };
    const vals = editValues[section.id] || {};
    const errs = sectionErrors[section.id] || {};

    // --- Global Section Content Validation ---
    const arraySections = ['hero', 'usp', 'grid', 'serviceCard', 'productCard', 'testimonial', 'clientLogos', 'team', 'faq', 'blog', 'problemSolution', 'founder'];
    if (arraySections.includes(section.type) && Array.isArray(section.content)) {
      const normalizedItems = section.content.map(item => {
        if (typeof item === 'object' && item !== null) {
          return JSON.stringify(item);
        }
        if (typeof item === 'string') {
          return item;
        }
        return null;
      }).filter((item): item is string => item !== null);

      section.content = normalizedItems;
    }

    switch (section.type) {
      case 'hero': {
        const slides: string[] = Array.isArray(section.content) ? section.content : [];
        return (
          <HeroEditor
            slides={slides}
            editing={editingSection === section.id}
            onChange={(newSlides) =>
              updateSectionContent(section.id, newSlides.map((s) => JSON.stringify(s)))
            }
          />
        );
      }

      case 'usp': {
        const items = Array.isArray(section.content) ? section.content : [];
        if (items.length === 0 && editingSection !== section.id) {
          return (
            <div className="bg-gray-50/70 border border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center">
              <p className="text-xs text-gray-500">No USP features configured yet. Click "Generate Section" or "Edit" to add key features.</p>
            </div>
          );
        }
        return (
            <div className="space-y-4">
              {items.map((item, idx) => {
                const parsed = parseJSONSafely(item);
                const titleKey = `usp_${idx}_title`;
                const descKey = `usp_${idx}_desc`;
                const iconKey = `usp_${idx}_icon`;
                const titleVal = String(editingSection === section.id ? (editValues[section.id]?.[titleKey] ?? parsed.title) : parsed.title || '');
                const descVal = String(editingSection === section.id ? (editValues[section.id]?.[descKey] ?? parsed.description) : parsed.description || '');
                const iconVal = String(editingSection === section.id ? (editValues[section.id]?.[iconKey] ?? parsed.icon) : parsed.icon || 'ri-checkbox-circle-line');

                return (
                  <div key={idx} className="flex gap-3 items-start bg-white/60 border border-gray-100 rounded-lg p-3 relative group">
                    {editingSection === section.id && (
                      <button
                        onClick={() => deleteUspItem(section.id, idx)}
                        className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 transition-colors cursor-pointer opacity-0 group-hover:opacity-100 z-10"
                        title="Delete card"
                      >
                        <i className="ri-delete-bin-line text-xs"></i>
                      </button>
                    )}
                    <div className="w-8 h-8 rounded-full bg-[#0b7f8f]/5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${iconVal} text-[#0b7f8f] text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      {editingSection === section.id ? (
                        <div className="pr-6">
                          <input
                            className={`w-full text-sm font-semibold text-gray-900 rounded px-2 py-1 focus:outline-none border ${getInputClass(section.id, titleKey, '')}`}
                            value={titleVal}
                            onChange={(e) => updateEditValue(section.id, titleKey, e.target.value, LIMITS.uspTitle.max)}
                            maxLength={LIMITS.uspTitle.max}
                            placeholder="Feature Title"
                          />
                          <CharLimitLabel current={titleVal.length} min={LIMITS.uspTitle.min} max={LIMITS.uspTitle.max} />
                          {errs[titleKey] && (
                            <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                              <i className="ri-error-warning-line"></i>{errs[titleKey].message}
                            </p>
                          )}
                          <textarea
                            className={`w-full text-xs text-gray-600 rounded px-2 py-1 focus:outline-none resize-none mt-1 border ${getInputClass(section.id, descKey, '')}`}
                            rows={2}
                            value={descVal}
                            onChange={(e) => updateEditValue(section.id, descKey, e.target.value, LIMITS.uspDesc.max)}
                            maxLength={LIMITS.uspDesc.max}
                            placeholder="Brief benefit description"
                          />
                          <CharLimitLabel current={descVal.length} min={LIMITS.uspDesc.min} max={LIMITS.uspDesc.max} />
                          {errs[descKey] && (
                            <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                              <i className="ri-error-warning-line"></i>{errs[descKey].message}
                            </p>
                          )}
                          <div className="mt-2">
                            <label className="block text-xs text-gray-500 mb-1">Icon</label>
                            <IconPicker
                              value={iconVal}
                              onChange={(val) => updateEditValue(section.id, iconKey, val, 50)}
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          <h4 className="text-sm font-semibold text-gray-900">{String(parsed.title || '')}</h4>
                          <p className="text-xs text-gray-600 leading-relaxed mt-0.5">{String(parsed.description || '')}</p>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {editingSection === section.id && (
                <button
                  onClick={() => addUspItem(section.id)}
                  className="mt-3 w-full py-2.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-gold hover:border-gold transition-colors flex items-center justify-center gap-2 cursor-pointer"
                >
                  <i className="ri-add-line"></i>
                  Add Card
                </button>
              )}
            </div>
        );
      }

      case 'story': {
        let extracted = '';
        if (typeof section.content === 'string') extracted = section.content;
        else if (Array.isArray(section.content) && section.content.length > 0) {
          const first = section.content[0];
          if (typeof first === 'string') extracted = first;
          else if (typeof first === 'object' && first !== null) extracted = first.text || first.story || '';
        }
        else if (typeof section.content === 'object' && section.content !== null) {
          // @ts-ignore
          extracted = section.content.text || section.content.story || section.content.content || '';
        }
        const storyVal = editingSection === section.id ? (vals.text ?? extracted) : extracted;
        if (!storyVal && editingSection !== section.id) {
          return (
            <div className="bg-gray-50/70 border border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center">
              <p className="text-xs text-gray-500">No content written yet. Click "Generate Section" or "Edit" to write your story.</p>
            </div>
          );
        }
        return (
            <div className="bg-white/60 border border-gray-100 rounded-lg p-4">
              {editingSection === section.id ? (
                <>
                  <RichTextEditor
                    html={storyVal}
                    onChange={(val) => updateEditValue(section.id, 'text', val, LIMITS.story.max)}
                    placeholder="Tell your brand's journey..."
                  />
                  <div className="mt-2">
                    <CharLimitLabel current={storyVal?.length || 0} min={LIMITS.story.min} max={LIMITS.story.max} />
                    {errs['text'] && (
                      <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>{errs['text'].message}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div 
                  className="text-sm text-gray-600 leading-relaxed prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: String(extracted || '') }}
                />
              )}
            </div>
        );
      }

      case 'grid': {
        const cards = Array.isArray(section.content) ? section.content : [];
        const isEditing = editingSection === section.id;
        if (cards.length === 0 && !isEditing) {
          return (
            <div className="bg-gray-50/70 border border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center">
              <p className="text-xs text-gray-500">No items configured yet. Click "Generate Section" or "Edit" to add cards.</p>
            </div>
          );
        }
        return (
          <div>
            <div className="grid grid-cols-3 gap-3">
              {cards.map((card, idx) => {
                const parsed = parseJSONSafely(card, { title: '', description: '', icon: 'ri-briefcase-line' });
                const titleKey = `${idx}_title`;
                const descKey  = `${idx}_desc`;
                const iconKey  = `${idx}_icon`;
                const titleVal = String(isEditing ? (vals[titleKey] ?? parsed.title) : parsed.title || '');
                const descVal  = String(isEditing ? (vals[descKey] ?? parsed.description) : parsed.description || '');
                const iconVal  = String(isEditing ? (vals[iconKey] ?? parsed.icon) : parsed.icon || 'ri-briefcase-line');

                return (
                  <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4 relative group">
                    {isEditing && (
                      <button
                        onClick={() => deleteGridCard(section.id, idx)}
                        className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
                        title="Delete card"
                      >
                        <i className="ri-delete-bin-line text-xs"></i>
                      </button>
                    )}
                    <div className="w-8 h-8 mb-2 bg-[#0b7f8f]/5 rounded-lg flex items-center justify-center">
                      <i className={`${iconVal} text-[#0b7f8f] text-sm`}></i>
                    </div>
                    {isEditing ? (
                      <>
                        <input
                          className={`w-full text-sm font-medium text-gray-900 rounded px-2 py-1 focus:outline-none mb-1 border ${getInputClass(section.id, titleKey, '')}`}
                          value={titleVal}
                          onChange={(e) => updateEditValue(section.id, titleKey, e.target.value, LIMITS.gridTitle.max)}
                          maxLength={LIMITS.gridTitle.max}
                        />
                        <CharLimitLabel current={titleVal.length} min={LIMITS.gridTitle.min} max={LIMITS.gridTitle.max} />
                        {errs[titleKey] && (
                          <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                            <i className="ri-error-warning-line"></i>
                            {errs[titleKey].message}
                          </p>
                        )}
                        <textarea
                          className={`w-full text-xs text-gray-600 rounded px-2 py-1 focus:outline-none resize-none mt-1 border ${getInputClass(section.id, descKey, '')}`}
                          rows={2}
                          value={descVal}
                          onChange={(e) => updateEditValue(section.id, descKey, e.target.value, LIMITS.gridDesc.max)}
                          maxLength={LIMITS.gridDesc.max}
                        />
                        <CharLimitLabel current={descVal.length} min={LIMITS.gridDesc.min} max={LIMITS.gridDesc.max} />
                        {errs[descKey] && (
                          <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                            <i className="ri-error-warning-line"></i>
                            {errs[descKey].message}
                          </p>
                        )}
                        <div className="mt-2">
                          <label className="block text-xs text-gray-500 mb-1">Icon</label>
                          <IconPicker
                            value={iconVal}
                            onChange={(val) => updateEditValue(section.id, iconKey, val, 50)}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <h4 className="text-sm font-medium text-gray-900 mb-1">{String(parsed.title || '')}</h4>
                        <p className="text-xs text-gray-600 leading-relaxed">{String(parsed.description || '')}</p>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            {isEditing && (
              <button
                onClick={() => addGridCard(section.id)}
                className="mt-3 w-full py-2.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-gold hover:border-gold transition-colors flex items-center justify-center gap-2 cursor-pointer"
              >
                <i className="ri-add-line"></i>
                Add Card
              </button>
            )}
          </div>
        );
      }

      case 'gallery': {
        const isHomeGallery = selectedPageId === 'home';
        // Home page pulls from Gallery page; Gallery page uses its own content
        const rawItems = isHomeGallery
          ? (Array.isArray(galleryPageItemsSection?.content) ? galleryPageItemsSection.content : [])
          : (Array.isArray(section.content) ? section.content : []);

        if (isHomeGallery && rawItems.length === 0) {
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <i className="ri-error-warning-line text-amber-600 mt-0.5"></i>
              <div>
                <p className="text-sm font-medium text-amber-800">Gallery feed unavailable</p>
                <p className="text-xs text-amber-700 mt-1">
                  Add images to the Gallery page before they can appear here.
                </p>
                <button
                  onClick={() => { if (editingSection) toggleEdit(editingSection); setSelectedPageId('gallery'); }}
                  className="mt-2 text-xs text-amber-800 underline hover:text-amber-900 cursor-pointer"
                >
                  Go to Gallery page →
                </button>
              </div>
            </div>
          );
        }

        const isEditing = editingSection === section.id;
        if (!isHomeGallery && rawItems.length === 0 && !isEditing) {
          return (
            <div className="bg-gray-50/70 border border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center">
              <p className="text-xs text-gray-500">No gallery images uploaded yet. Click "Generate Section" or "Edit" to upload gallery photos.</p>
            </div>
          );
        }
        return (
          <GalleryEditor
            content={isHomeGallery && !isEditing ? rawItems.slice(0, 3) : rawItems}
            editing={isEditing}
            onChange={(newContent) => {
              if (isHomeGallery && galleryPageItemsSection) {
                 // Update the target gallery page's gallery section directly
                 onPagesChange(prev =>
                    prev.map(page =>
                       page.pageId === 'gallery'
                          ? {
                               ...page,
                               sections: page.sections.map(s =>
                                  s.type === 'gallery'
                                     ? { ...s, content: newContent }
                                     : s
                               )
                            }
                          : page
                    )
                 );
              } else {
                 updateSectionContent(section.id, newContent);
              }
            }}
            validateImageFile={validateImageFile}
          />
        );
      }

      case 'blog': {
        const isHomeBlog = selectedPageId === 'home';
        // Home page pulls from Blog page; Blog page uses its own content
        const rawPosts = isHomeBlog
          ? (Array.isArray(blogPagePosts?.content) ? blogPagePosts.content : [])
          : (Array.isArray(section.content) ? section.content : []);

        if (isHomeBlog && !hasEnoughBlogs) {
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3"
            >
              <i className="ri-error-warning-line text-amber-600 mt-0.5"
              >
              </i>
              <div>
                <p className="text-sm font-medium text-amber-800"
                >Blog feed unavailable</p>
                <p className="text-xs text-amber-700 mt-1"
                >
                  At least 3 blogs are required on the Blog page before they can appear here. Currently {blogPostsCount} of 3.
                </p>
                <button
                  onClick={() => { if (editingSection) toggleEdit(editingSection); setSelectedPageId('blog'); }}
                  className="mt-2 text-xs text-amber-800 underline hover:text-amber-900 cursor-pointer"
                >
                  Go to Blog page →
                </button>
              </div>
            </div>
          );
        }

        if (!isHomeBlog && rawPosts.length === 0 && editingSection !== section.id) {
          return (
            <div className="bg-gray-50/70 border border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center">
              <p className="text-xs text-gray-500">No blog posts available yet. Click "Generate Section" or "Edit" to create articles.</p>
            </div>
          );
        }

        return (
          <div className="space-y-3">
            {(isHomeBlog ? rawPosts.slice(0, 3) : rawPosts).map((post, idx) => {
              const parsed = parseJSONSafely(post, { title: '', image: '', date: '', content: '', image_status: '' });

              return (
                <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4 flex gap-3 relative group"
                >
                  {!isHomeBlog && editingSection === section.id && (
                    <button
                      onClick={() => deleteBlogPost(section.id, idx)}
                      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 transition-colors cursor-pointer opacity-0 group-hover:opacity-100 z-10"
                      title="Delete post"
                    >
                      <i className="ri-delete-bin-line text-xs"></i>
                    </button>
                  )}
                  <div className="w-16 h-12 bg-navy/5 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden relative"
                  >
                    {parsed.image ? (
                      <img src={parsed.image} alt={parsed.title} className="w-full h-full object-cover" />
                    ) : parsed.image_status === 'pending' ? (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-amber-50/60 text-gold animate-pulse" title="Generating AI image...">
                        <i className="ri-loader-4-line text-lg animate-spin"></i>
                      </div>
                    ) : (
                      <i className="ri-article-line text-navy text-lg"></i>
                    )}
                  </div>
                  <div className="flex-1 min-w-0"
                  >
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">{String(parsed.title || 'Untitled Post')}</h4>
                    <div className="flex items-center gap-2 text-[10px] text-gray-400 mb-2">
                      <span>{String(parsed.date || 'No Date')}</span>
                      <span>•</span>
                      <span>5 min read</span>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed line-clamp-3">{generateExcerpt(parsed.content || '')}</p>
                    
                    {!isHomeBlog && editingSection === section.id && (
                      <button
                        onClick={() => openBlogEditor(section.id, idx)}
                        className="mt-3 text-xs text-gold hover:text-gold-light transition-colors flex items-center gap-1 cursor-pointer"
                      >
                        <i className="ri-edit-line"></i>
                        Edit Post
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {!isHomeBlog && editingSection === section.id && (
              <button
                onClick={() => addBlogPost(section.id)}
                className="w-full py-2.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-gold hover:border-gold transition-colors flex items-center justify-center gap-2 cursor-pointer"
              >
                <i className="ri-add-line"></i>
                Add Blog Post
              </button>
            )}
            {!isHomeBlog && editingSection === section.id && (
              <div className="text-xs text-gray-500 mt-1">
                {rawPosts.length} posts · Min 3 required
                {rawPosts.length < 3 && (
                  <span className="text-red-500 ml-1">· Need {3 - rawPosts.length} more</span>
                )}
              </div>
            )}
            {isHomeBlog && rawPosts.length > 3 && (
              <div className="text-center pt-1"
              >
                <button
                  onClick={() => { if (editingSection) toggleEdit(editingSection); setSelectedPageId('blog'); }}
                  className="text-xs text-gold hover:text-gold-light cursor-pointer"
                >
                  View all {rawPosts.length} articles →
                </button>
              </div>
            )}
          </div>
        );
      }

      case 'contact':
      case 'contactInfo': {
        const isEditing = editingSection === section.id;
        const info = globalContactInfo || { email: '', phone: '', location: '', description: '', socials: {} };
        const editDesc = info.description || '';
        const editEmail = info.email || '';
        const editPhone = info.phone || '';
        const editLocation = info.location || '';

        return (
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            {isEditing ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Email Address</label>
                    <input
                      type="text"
                      className={`w-full text-sm text-gray-700 rounded-lg px-4 py-2 focus:outline-none border ${getInputClass(section.id, 'email', '')}`}
                      value={editEmail}
                      onChange={(e) => setGlobalContactInfo(prev => ({...prev, email: e.target.value}))}
                      placeholder="Enter your email"
                    />
                    {errs['email'] && (
                      <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{errs['email'].message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Phone Number</label>
                    <input
                      type="text"
                      className={`w-full text-sm text-gray-700 rounded-lg px-4 py-2 focus:outline-none border ${getInputClass(section.id, 'phone', '')}`}
                      value={editPhone}
                      onChange={(e) => setGlobalContactInfo(prev => ({...prev, phone: e.target.value}))}
                      placeholder="Add your mobile number"
                    />
                    {errs['phone'] && (
                      <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{errs['phone'].message}</p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Business Location</label>
                    <input
                      type="text"
                      className={`w-full text-sm text-gray-700 rounded-lg px-4 py-2 focus:outline-none border ${getInputClass(section.id, 'location', '')}`}
                      value={editLocation}
                      onChange={(e) => setGlobalContactInfo(prev => ({...prev, location: e.target.value}))}
                      placeholder="Add your location"
                    />
                    {errs['location'] && (
                      <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{errs['location'].message}</p>
                    )}
                  </div>
                </div>
                <label className="block text-xs text-gray-500 mb-1">Description</label>
                <textarea
                  className={`w-full text-sm text-gray-700 rounded-lg px-4 py-3 focus:outline-none resize-none leading-relaxed border ${getInputClass(section.id, 'description', '')}`}
                  rows={3}
                  value={editDesc}
                  onChange={(e) => setGlobalContactInfo(prev => ({...prev, description: e.target.value}))}
                  maxLength={LIMITS.contact.max}
                  placeholder="Describe how customers can reach you..."
                />
                <CharLimitLabel current={editDesc.length} min={LIMITS.contact.min} max={LIMITS.contact.max} />
              </>
            ) : (
              <>
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Contact Information</h3>
                <p className="text-xs text-gray-500 mb-6 leading-relaxed">{String(editDesc)}</p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <div className="w-8 h-8 rounded-full bg-[#0b7f8f]/5 flex items-center justify-center text-[#0b7f8f]"><i className="ri-mail-line"></i></div>
                    {editEmail ? String(editEmail) : <span className="text-gray-400 italic">Enter your email</span>}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <div className="w-8 h-8 rounded-full bg-[#0b7f8f]/5 flex items-center justify-center text-[#0b7f8f]"><i className="ri-phone-line"></i></div>
                    {editPhone ? String(editPhone) : <span className="text-gray-400 italic">Add your mobile number</span>}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <div className="w-8 h-8 rounded-full bg-[#0b7f8f]/5 flex items-center justify-center text-[#0b7f8f]"><i className="ri-map-pin-line"></i></div>
                    {editLocation ? String(editLocation) : <span className="text-gray-400 italic">Add your location</span>}
                  </div>
                </div>
              </>
            )}
          </div>
        );
      }
      case 'contactForm': {
        const parsed = parseJSONSafely(section.content, { heading: 'Contact Us', subtext: 'Fill out the form below and our team will get back to you as soon as possible.' });
        const isEditing = editingSection === section.id;
        const headingVal = isEditing ? (vals.heading ?? parsed.heading) : parsed.heading;
        const subtextVal = isEditing ? (vals.subtext ?? parsed.subtext) : parsed.subtext;
        
        const location = globalContactInfo?.location;
        const mapHtml = location ? (
          <div className="mt-8 rounded-lg overflow-hidden h-[300px]">
            <iframe 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              scrolling="no" 
              marginHeight={0} 
              marginWidth={0} 
              src={`https://maps.google.com/maps?q=${encodeURIComponent(location)}&output=embed`}
            />
          </div>
        ) : null;

        return (
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
              {isEditing ? (
                <input
                  type="text"
                  className="w-full text-sm font-semibold text-gray-900 border border-gold focus:outline-none rounded px-2 py-1 mb-1"
                  value={headingVal}
                  onChange={(e) => setVals(prev => ({ ...prev, heading: e.target.value }))}
                />
              ) : (
                <h3 className="text-sm font-semibold text-gray-800 mb-1">{String(headingVal)}</h3>
              )}
              
              {isEditing ? (
                <textarea
                  className="w-full text-xs text-gray-700 border border-gold focus:outline-none rounded px-2 py-1 mb-4"
                  value={subtextVal}
                  onChange={(e) => setVals(prev => ({ ...prev, subtext: e.target.value }))}
                />
              ) : (
                <p className="text-xs text-gray-500 mb-4">{String(subtextVal)}</p>
              )}
              
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div><label className="block text-[11px] text-gray-500 mb-1">Full Name</label><div className="bg-white rounded-md px-3 py-2 text-sm text-gray-400 border border-gray-200">John Doe</div></div>
                <div><label className="block text-[11px] text-gray-500 mb-1">Email</label><div className="bg-white rounded-md px-3 py-2 text-sm text-gray-400 border border-gray-200">{globalContactInfo.email || 'Enter your email'}</div></div>
                <div><label className="block text-[11px] text-gray-500 mb-1">Phone</label><div className="bg-white rounded-md px-3 py-2 text-sm text-gray-400 border border-gray-200">{globalContactInfo.phone || 'Add your mobile number'}</div></div>
                <div><label className="block text-[11px] text-gray-500 mb-1">Company</label><div className="bg-white rounded-md px-3 py-2 text-sm text-gray-400 border border-gray-200">Acme Inc.</div></div>
              </div>
              <div className="mb-4"><label className="block text-[11px] text-gray-500 mb-1">Message</label><div className="bg-white rounded-md px-3 py-3 text-sm text-gray-400 border border-gray-200 min-h-[80px]">How can we help you?</div></div>
              <button className="bg-[#e8f9fb] text-[#0b7f8f] border border-[#cdeff3] text-sm font-medium px-5 py-2 rounded-lg cursor-not-allowed opacity-80 whitespace-nowrap">Send Message</button>
              {mapHtml}
            </div>
          </div>
        );
      }

      case 'founder': {
        const members = Array.isArray(section.content) ? section.content : (section.content ? [section.content] : []);
        const member = members[0];
        const parsed = parseJSONSafely(member, { name: '', role: '', bio: '', image: '' });
        
        const isEditing = editingSection === section.id;
        const nameVal = isEditing ? (vals['0_name'] ?? parsed.name ?? '') : (parsed.name ?? '');
        const roleVal = isEditing ? (vals['0_role'] ?? parsed.role ?? '') : (parsed.role ?? '');
        const bioVal = isEditing ? (vals['0_bio'] ?? parsed.bio ?? '') : (parsed.bio ?? '');
        const imageVal = isEditing ? (vals['0_image'] ?? parsed.image ?? '') : (parsed.image ?? '');

        if (!isEditing && !nameVal && !roleVal && !bioVal && !imageVal) {
          return (
            <div className="bg-gray-50/70 border border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center">
              <p className="text-xs text-gray-500">Founder profile not yet configured. Click "Generate Section" or "Edit" to add founder details.</p>
            </div>
          );
        }

        return (
          <div className="flex flex-col gap-4">
            <div className="flex gap-5">
              <div className="w-[200px] flex-shrink-0">
                <div className="aspect-[4/5] bg-gray-100 rounded-xl overflow-hidden relative group">
                  {parsed.image_status === 'pending' && !imageVal ? (
                    <div className="w-full h-full flex flex-col items-center justify-center text-[#0b7f8f] gap-3 bg-gray-50 animate-pulse">
                      <i className="ri-loader-4-line animate-spin text-3xl"></i>
                      <span className="text-[11px] font-medium text-gray-400">Generating image...</span>
                    </div>
                  ) : imageVal ? (
                    <img src={imageVal} alt={nameVal || 'Founder'} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 gap-2">
                      <i className="ri-user-line text-4xl"></i>
                      <span className="text-[11px] text-gray-400">Upload a photo</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-0">
              {isEditing ? (
                <div className="space-y-3">
                  <div>
                    <input
                      className={`w-full text-base font-semibold text-gray-900 rounded px-2 py-1 focus:outline-none border ${getInputClass(section.id, '0_name', '')}`}
                      value={nameVal}
                      onChange={(e) => updateEditValue(section.id, '0_name', e.target.value, LIMITS.founderName.max)}
                      maxLength={LIMITS.founderName.max}
                      placeholder="Founder Name"
                    />
                    <CharLimitLabel current={nameVal.length} min={LIMITS.founderName.min} max={LIMITS.founderName.max} />
                    {errs['0_name'] && (
                      <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        {errs['0_name'].message}
                      </p>
                    )}
                  </div>
                  <div>
                    <input
                      className={`w-full text-sm text-[#0b7f8f] rounded px-2 py-1 focus:outline-none border ${getInputClass(section.id, '0_role', '')}`}
                      value={roleVal}
                      onChange={(e) => updateEditValue(section.id, '0_role', e.target.value, LIMITS.founderRole.max)}
                      maxLength={LIMITS.founderRole.max}
                      placeholder="Role / Title (e.g. Founder & CEO)"
                    />
                    <CharLimitLabel current={roleVal.length} min={LIMITS.founderRole.min} max={LIMITS.founderRole.max} />
                    {errs['0_role'] && (
                      <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        {errs['0_role'].message}
                      </p>
                    )}
                  </div>
                  <div>
                    <textarea
                      className={`w-full text-sm text-gray-700 rounded px-2 py-2 focus:outline-none resize-none leading-relaxed border ${getInputClass(section.id, '0_bio', '')}`}
                      rows={4}
                      value={bioVal}
                      onChange={(e) => updateEditValue(section.id, '0_bio', e.target.value, LIMITS.founderBio.max)}
                      maxLength={LIMITS.founderBio.max}
                      placeholder="Short bio about the founder..."
                    />
                    <CharLimitLabel current={bioVal.length} min={LIMITS.founderBio.min} max={LIMITS.founderBio.max} />
                    {errs['0_bio'] && (
                      <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        {errs['0_bio'].message}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <h4 className="text-base font-semibold text-gray-900">{nameVal || <span className="text-gray-400 italic">Founder Name</span>}</h4>
                  <p className="text-sm text-[#0b7f8f] mt-0.5">{roleVal || <span className="text-gray-400 italic">Founder & CEO</span>}</p>
                  <p className="text-sm text-gray-700 leading-relaxed mt-2">{bioVal || <span className="text-gray-400 italic">Founder biography will appear here once entered.</span>}</p>
                </div>
              )}
            </div>
            </div>
            {isEditing && (
              <div className="mt-2">
                <ImageUploader 
                  type="founder" 
                  currentImage={imageVal} 
                  onUpload={(file, url) => updateEditValue(section.id, '0_image', url)} 
                />
              </div>
            )}
          </div>
        );
      }

      case 'team': {
        const members = Array.isArray(section.content) ? section.content : [];
        const isEditing = editingSection === section.id;
        if (members.length === 0 && !isEditing) {
          return (
            <div className="bg-gray-50/70 border border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center">
              <p className="text-xs text-gray-500">No team members added yet. Click "Generate Section" or "Edit" to add team members.</p>
            </div>
          );
        }

        return (
          <div>
            <div className={isEditing ? "flex flex-col gap-6" : "grid grid-cols-3 gap-4"}>
              {members.map((member, idx) => {
                const parsed = parseJSONSafely(member, { name: '', role: '', bio: '', image: '' });
                const nameKey = `${idx}_name`;
                const roleKey = `${idx}_role`;
                const bioKey = `${idx}_bio`;
                const imageKey = `${idx}_image`;
                const nameVal = String(isEditing ? (vals[nameKey] ?? parsed.name) : (parsed.name || ''));
                const roleVal = String(isEditing ? (vals[roleKey] ?? parsed.role) : (parsed.role || ''));
                const bioVal = String(isEditing ? (vals[bioKey] ?? parsed.bio) : (parsed.bio || ''));
                const imageVal = isEditing ? (vals[imageKey] ?? parsed.image) : parsed.image;

                if (isEditing) {
                  return (
                    <div key={idx} className="bg-white border border-gray-200 rounded-xl p-4 relative group flex flex-col gap-3">
                      <button
                        onClick={() => deleteTeamMember(section.id, idx)}
                        className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 transition-colors cursor-pointer opacity-0 group-hover:opacity-100 z-10"
                        title="Delete member"
                      >
                        <i className="ri-delete-bin-line text-xs"></i>
                      </button>
                      <div className="flex gap-4">
                        <div className="w-20 h-20 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden relative">
                          {imageVal ? (
                            <img src={imageVal} alt={nameVal} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <i className="ri-user-line text-2xl"></i>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 space-y-2">
                          <input
                            className={`w-full text-sm font-semibold text-gray-900 rounded px-2 py-1 focus:outline-none border ${getInputClass(section.id, nameKey, '')}`}
                            value={nameVal}
                            onChange={(e) => updateEditValue(section.id, nameKey, e.target.value, LIMITS.teamName.max)}
                            maxLength={LIMITS.teamName.max}
                            placeholder="Name"
                          />
                          <CharLimitLabel current={nameVal.length} min={LIMITS.teamName.min} max={LIMITS.teamName.max} />
                          {errs[nameKey] && (
                            <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                              <i className="ri-error-warning-line"></i>
                              {errs[nameKey].message}
                            </p>
                          )}
                          <input
                            className={`w-full text-[11px] text-[#0b7f8f] rounded px-2 py-1 focus:outline-none border ${getInputClass(section.id, roleKey, '')}`}
                            value={roleVal}
                            onChange={(e) => updateEditValue(section.id, roleKey, e.target.value, LIMITS.teamRole.max)}
                            maxLength={LIMITS.teamRole.max}
                            placeholder="Role"
                          />
                          <CharLimitLabel current={roleVal.length} min={LIMITS.teamRole.min} max={LIMITS.teamRole.max} />
                          {errs[roleKey] && (
                            <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                              <i className="ri-error-warning-line"></i>
                              {errs[roleKey].message}
                            </p>
                          )}
                          <textarea
                            className={`w-full text-[11px] text-gray-600 rounded px-2 py-1 focus:outline-none resize-none border ${getInputClass(section.id, bioKey, '')}`}
                            rows={3}
                            value={bioVal}
                            onChange={(e) => updateEditValue(section.id, bioKey, e.target.value, LIMITS.teamBio.max)}
                            maxLength={LIMITS.teamBio.max}
                            placeholder="Short bio"
                          />
                          <CharLimitLabel current={bioVal.length} min={LIMITS.teamBio.min} max={LIMITS.teamBio.max} />
                          {errs[bioKey] && (
                            <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                              <i className="ri-error-warning-line"></i>
                              {errs[bioKey].message}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="mt-2">
                        <ImageUploader 
                          type="team" 
                          currentImage={imageVal} 
                          onUpload={(file, url) => updateEditValue(section.id, imageKey, url)} 
                        />
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={idx} className="bg-white border border-gray-200 rounded-xl p-4 relative group text-center">
                    <div className="w-full aspect-square bg-gray-100 rounded-lg overflow-hidden mb-3 relative group/img">
                      {parsed.image ? (
                        <img src={parsed.image} alt={parsed.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 gap-2">
                          <i className="ri-user-line text-3xl"></i>
                          <span className="text-[11px] text-gray-400">Upload a photo</span>
                        </div>
                      )}
                    </div>
                    <h4 className="text-sm font-semibold text-gray-900">{String(parsed.name || '')}</h4>
                    <p className="text-xs text-[#0b7f8f] font-medium mb-1">{String(parsed.role || '')}</p>
                    <p className="text-[11px] text-gray-500 leading-relaxed line-clamp-3">{String(parsed.bio || '')}</p>
                  </div>
                );
              })}
            </div>
            {isEditing && (
              <button
                onClick={() => addTeamMember(section.id)}
                className="mt-3 w-full py-2.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-gold hover:border-gold transition-colors flex items-center justify-center gap-2 cursor-pointer"
              >
                <i className="ri-add-line"></i>
                Add Team Member
              </button>
            )}
          </div>
        );
      }

      case 'problemSolution':
      case 'serviceCard':
      case 'productCard':
      case 'testimonial':
      case 'clientLogos':
      case 'faq': {
        return (
          <ServiceProductSection
            section={section}
            editing={editingSection === section.id}
            editValues={editValues[section.id] || {}}
            errors={sectionErrors[section.id] || {}}
            onEditValueChange={(key, value, max) => updateEditValue(section.id, key, value, max)}
            onAddItem={
              (section.type === 'serviceCard' || section.type === 'productCard')
                ? () => addServiceItem(section.id)
                : section.type === 'testimonial'
                ? () => addTestimonial(section.id)
                : section.type === 'clientLogos'
                ? () => addClientLogo(section.id)
                : section.type === 'faq'
                ? () => addFaq(section.id)
                : undefined
            }
            onDeleteItem={
              (section.type === 'serviceCard' || section.type === 'productCard')
                ? (idx) => deleteServiceItem(section.id, idx)
                : section.type === 'testimonial'
                ? (idx) => deleteTestimonial(section.id, idx)
                : section.type === 'clientLogos'
                ? (idx) => deleteClientLogo(section.id, idx)
                : section.type === 'faq'
                ? (idx) => deleteFaq(section.id, idx)
                : undefined
            }
            onImageUpload={(key, file, url) => handleImageUpload(section.id, key, url)}
          />
        );
      }

      case 'custom':
      case 'customSection': {
        const isEditing = editingSection === section.id;
        const normalized = normalizeCustomSection(section.content);
        const currentLayout: CustomSectionLayout = (isEditing && vals.custom_layout)
          ? (vals.custom_layout as CustomSectionLayout)
          : (normalized.layout === 'cardGrid' || normalized.layout === 'alternating' ? normalized.layout : 'story');

        let editBlocks: CustomSectionBlock[] = [];
        if (isEditing && vals.custom_blocks) {
          try {
            editBlocks = JSON.parse(vals.custom_blocks);
          } catch {
            editBlocks = normalized.blocks;
          }
        } else {
          editBlocks = normalized.blocks;
        }

        if (editBlocks.length === 0) {
          editBlocks = [createDefaultBlock(1)];
        }

        if (isEditing) {
          const layouts: { id: CustomSectionLayout; name: string; icon: string; desc: string }[] = [
            { id: 'story', name: 'Story', icon: 'ri-book-open-line', desc: 'Narrative with media' },
            { id: 'cardGrid', name: 'Card Grid', icon: 'ri-grid-fill', desc: '2–4 column cards' },
            { id: 'alternating', name: 'Alternating', icon: 'ri-split-cells-horizontal', desc: 'Zigzag rows' },
          ];

          return (
            <div className="space-y-6">
              {/* Layout Selector Bar */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wider">
                  Select Section Layout
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {layouts.map(l => {
                    const isActive = currentLayout === l.id;
                    return (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => setCustomLayout(section.id, l.id)}
                        className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between ${
                          isActive
                            ? 'bg-teal-50/80 border-teal-500 ring-2 ring-teal-500/20 shadow-xs'
                            : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50/60'
                        }`}
                      >
                        <div className="flex items-center justify-between w-full mb-2">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            isActive ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'
                          }`}>
                            <i className={`${l.icon} text-base`}></i>
                          </div>
                          {isActive && (
                            <span className="w-2.5 h-2.5 rounded-full bg-teal-500"></span>
                          )}
                        </div>
                        <div>
                          <div className={`text-xs font-bold ${isActive ? 'text-teal-900' : 'text-gray-800'}`}>
                            {l.name}
                          </div>
                          <div className="text-[11px] text-gray-500 mt-0.5 leading-tight">
                            {l.desc}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Block List Editor */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Content Blocks ({editBlocks.length}/{MAX_CUSTOM_BLOCKS})
                  </label>
                  <span className="text-[11px] text-gray-500">
                    Drag handle <i className="ri-draggable align-middle"></i> or use arrows to reorder
                  </span>
                </div>

                <div className="space-y-4">
                  {editBlocks.map((b, idx) => {
                    const isOver = dragOverBlock?.sectionId === section.id && dragOverBlock?.blockIdx === idx;
                    const mediaType: CustomBlockMediaType = b.mediaType || (b.image ? 'image' : 'icon');
                    return (
                      <div
                        key={b.id || idx}
                        draggable
                        onDragStart={(e) => handleBlockDragStart(e, section.id, idx)}
                        onDragOver={(e) => handleBlockDragOver(e, section.id, idx)}
                        onDrop={(e) => handleBlockDrop(e, section.id, idx)}
                        onDragEnd={handleBlockDragEnd}
                        className={`bg-white border rounded-xl p-4 transition-all ${
                          isOver ? 'border-teal-500 ring-2 ring-teal-400/30 shadow-md' : 'border-gray-200 shadow-xs'
                        }`}
                      >
                        {/* Block Header */}
                        <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100">
                          <div className="flex items-center gap-2">
                            <span
                              className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                              title="Drag to reorder block"
                            >
                              <i className="ri-draggable text-sm"></i>
                            </span>
                            <span className="text-xs font-bold text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                              Block #{idx + 1}
                            </span>
                          </div>

                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => moveCustomBlock(section.id, idx, idx - 1)}
                              disabled={idx === 0}
                              className={`p-1 rounded text-xs ${
                                idx === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100 cursor-pointer'
                              }`}
                              title="Move Block Up"
                            >
                              <i className="ri-arrow-up-s-line"></i>
                            </button>
                            <button
                              type="button"
                              onClick={() => moveCustomBlock(section.id, idx, idx + 1)}
                              disabled={idx === editBlocks.length - 1}
                              className={`p-1 rounded text-xs ${
                                idx === editBlocks.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100 cursor-pointer'
                              }`}
                              title="Move Block Down"
                            >
                              <i className="ri-arrow-down-s-line"></i>
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteCustomBlock(section.id, idx)}
                              className="p-1 rounded text-xs text-red-500 hover:bg-red-50 cursor-pointer ml-1"
                              title="Delete Block"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        </div>

                        {/* Block Inputs */}
                        <div className="space-y-3.5">
                          {/* Visual Media Selection: Icon vs Image vs None */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-[11px] font-semibold text-gray-700 uppercase tracking-wider">
                                Block Visual Media
                              </label>
                              <div className="inline-flex rounded-lg bg-gray-100 p-0.5 border border-gray-200 text-xs">
                                {(['icon', 'image', 'none'] as const).map(type => {
                                  const isSelected = mediaType === type;
                                  return (
                                    <button
                                      key={type}
                                      type="button"
                                      onClick={() => updateCustomBlock(section.id, idx, 'mediaType', type)}
                                      className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors cursor-pointer capitalize ${
                                        isSelected
                                          ? 'bg-white text-teal-900 shadow-xs font-semibold'
                                          : 'text-gray-600 hover:text-gray-900'
                                      }`}
                                    >
                                      {type === 'icon' && <i className="ri-remixicon-line mr-1 align-middle"></i>}
                                      {type === 'image' && <i className="ri-image-line mr-1 align-middle"></i>}
                                      {type === 'none' && <i className="ri-forbid-line mr-1 align-middle"></i>}
                                      {type}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Render IconPicker or ImageUploader */}
                            {mediaType === 'icon' ? (
                              <div className="space-y-3 p-3 bg-gray-50/80 rounded-lg border border-gray-200">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                  <div className="inline-flex rounded-md bg-gray-200/70 p-0.5 text-xs">
                                    <button
                                      type="button"
                                      onClick={() => updateCustomBlock(section.id, idx, 'iconType', 'preset')}
                                      className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors cursor-pointer ${
                                        (b.iconType || 'preset') === 'preset'
                                          ? 'bg-white text-teal-900 shadow-xs font-semibold'
                                          : 'text-gray-600 hover:text-gray-900'
                                      }`}
                                    >
                                      <i className="ri-remixicon-line mr-1 align-middle"></i>
                                      Preset Icon
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => updateCustomBlock(section.id, idx, 'iconType', 'custom')}
                                      className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors cursor-pointer ${
                                        b.iconType === 'custom'
                                          ? 'bg-white text-teal-900 shadow-xs font-semibold'
                                          : 'text-gray-600 hover:text-gray-900'
                                      }`}
                                    >
                                      <i className="ri-upload-cloud-2-line mr-1 align-middle"></i>
                                      Upload Custom Icon
                                    </button>
                                  </div>

                                  <span className="text-[11px] text-gray-500 italic">
                                    {b.iconType === 'custom'
                                      ? 'Recommended: 64 × 64 px (1:1 square) • SVG, PNG'
                                      : 'Select from 60+ icon presets'}
                                  </span>
                                </div>

                                {b.iconType === 'custom' ? (
                                  <div className="space-y-2">
                                    {b.customIcon && (
                                      <div className="flex items-center gap-3 p-2 bg-white rounded-lg border border-gray-200">
                                        <div className="w-10 h-10 rounded-lg bg-teal-50 border border-teal-100 flex items-center justify-center flex-shrink-0 p-1.5">
                                          <img src={b.customIcon} alt="Custom Icon" className="w-6 h-6 object-contain" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="text-xs font-semibold text-gray-800">Custom Icon Active</div>
                                          <div className="text-[11px] text-gray-500">Previewed in exact matching badge size</div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => updateCustomBlock(section.id, idx, 'customIcon', '')}
                                          className="p-1 rounded text-xs text-red-500 hover:bg-red-50 cursor-pointer"
                                          title="Remove Icon"
                                        >
                                          <i className="ri-delete-bin-line"></i>
                                        </button>
                                      </div>
                                    )}
                                    <ImageUploader
                                      type="customIcon"
                                      currentImage={b.customIcon || ''}
                                      buttonText={b.customIcon ? 'Replace Icon' : 'Upload Custom Icon'}
                                      onUpload={(_file, url) => updateCustomBlock(section.id, idx, 'customIcon', url)}
                                    />
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-teal-100/80 text-teal-700 flex items-center justify-center flex-shrink-0">
                                      <i className={`${b.icon || 'ri-star-line'} text-xl`}></i>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <IconPicker
                                        value={b.icon || 'ri-star-line'}
                                        onChange={(icon) => updateCustomBlock(section.id, idx, 'icon', icon)}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : mediaType === 'image' ? (
                              <div className="pt-1 space-y-2">
                                <div className="flex items-center justify-between text-[11px] text-gray-500">
                                  <span>Upload block image</span>
                                  <span className="italic">Recommended: 1200 × 800 px (3:2 landscape) • JPG, PNG, WEBP</span>
                                </div>
                                {b.image && (
                                  <div className="mb-2 max-w-xs aspect-video bg-gray-50 rounded-lg border border-gray-200 overflow-hidden relative group">
                                    <img src={b.image} alt={b.title || `Block ${idx + 1}`} className="w-full h-full object-cover" />
                                    <button
                                      type="button"
                                      onClick={() => updateCustomBlock(section.id, idx, 'image', '')}
                                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center text-xs opacity-80 hover:opacity-100 transition-opacity cursor-pointer"
                                      title="Remove image"
                                    >
                                      <i className="ri-delete-bin-line"></i>
                                    </button>
                                  </div>
                                )}
                                <ImageUploader
                                  type="custom"
                                  currentImage={b.image || ''}
                                  onUpload={(_file, url) => updateCustomBlock(section.id, idx, 'image', url)}
                                />
                              </div>
                            ) : (
                              <div className="p-2.5 text-center text-xs text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                                No icon or image will be shown for this block (Text Only).
                              </div>
                            )}
                          </div>

                          {/* Title */}
                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <label className="text-[11px] font-semibold text-gray-600">
                                Block Title (Optional)
                              </label>
                              <CharLimitLabel current={(b.title || '').length} max={100} />
                            </div>
                            <input
                              type="text"
                              value={b.title || ''}
                              maxLength={100}
                              onChange={(e) => updateCustomBlock(section.id, idx, 'title', e.target.value)}
                              placeholder={`e.g. ${currentLayout === 'cardGrid' ? 'Enterprise Support' : 'Our Mission'}`}
                              className="w-full px-3 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-hidden focus:ring-1 focus:ring-teal-500 focus:bg-white"
                            />
                          </div>

                          {/* Body Content */}
                          <div>
                            <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                              Block Content (Rich Text)
                            </label>
                            <RichTextEditor
                              html={b.body || ''}
                              onChange={(html) => updateCustomBlock(section.id, idx, 'body', html)}
                              placeholder="Write copy, description, bullet points..."
                              minHeight="120px"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Add Block Button */}
                <div className="mt-3">
                  {editBlocks.length < MAX_CUSTOM_BLOCKS ? (
                    <button
                      type="button"
                      onClick={() => addCustomBlock(section.id)}
                      className="w-full py-2.5 px-4 rounded-xl border border-dashed border-teal-500/60 bg-teal-50/40 hover:bg-teal-50 text-teal-700 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                    >
                      <i className="ri-add-line text-sm"></i>
                      <span>Add Block ({editBlocks.length}/{MAX_CUSTOM_BLOCKS})</span>
                    </button>
                  ) : (
                    <div className="text-center py-2 text-xs text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      <i className="ri-information-line mr-1"></i>
                      Maximum limit of {MAX_CUSTOM_BLOCKS} blocks reached for this section.
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }

        // Live Canvas Preview (Non-editing)
        const finalBlocks = normalized.blocks && normalized.blocks.length > 0 ? normalized.blocks : [createDefaultBlock(1)];

        if (currentLayout === 'cardGrid') {
          return (
            <div className="bg-white/80 border border-gray-100 rounded-xl p-6 shadow-xs">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {finalBlocks.map((b, idx) => {
                  const hasImage = (b.mediaType === 'image' || (!b.mediaType && b.image)) && b.image;
                  const hasIcon = b.mediaType === 'icon' || (!b.mediaType && !hasImage && b.mediaType !== 'none');
                  return (
                    <div key={b.id || idx} className="bg-gray-50/80 border border-gray-200/80 rounded-xl p-4 flex flex-col">
                      {hasImage ? (
                        <div className="aspect-video w-full rounded-lg overflow-hidden bg-gray-100 mb-3 border border-gray-200">
                          <img src={b.image} alt={b.title || `Card ${idx + 1}`} className="w-full h-full object-cover" />
                        </div>
                      ) : hasIcon ? (
                        <div className="w-10 h-10 rounded-xl bg-teal-100/70 border border-teal-200/60 text-teal-700 flex items-center justify-center mb-3 p-2 flex-shrink-0 shadow-2xs">
                          {b.iconType === 'custom' && b.customIcon ? (
                            <img src={b.customIcon} alt={b.title || 'Icon'} className="w-5 h-5 object-contain" />
                          ) : (
                            <i className={`${b.icon || 'ri-star-line'} text-lg`}></i>
                          )}
                        </div>
                      ) : null}
                      {b.title && (
                        <h4 className="text-sm font-semibold text-gray-900 mb-1.5">{b.title}</h4>
                      )}
                      <div className="text-xs text-gray-600 leading-relaxed flex-1 prose prose-xs max-w-none">
                        {b.body ? (
                          <div dangerouslySetInnerHTML={{ __html: b.body }} />
                        ) : (
                          <span className="italic text-gray-400">Empty block content</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        if (currentLayout === 'alternating') {
          return (
            <div className="bg-white/80 border border-gray-100 rounded-xl p-6 shadow-xs space-y-4">
              {finalBlocks.map((b, idx) => {
                const isEven = idx % 2 === 1;
                const hasImage = (b.mediaType === 'image' || (!b.mediaType && b.image)) && b.image;
                const hasIcon = b.mediaType === 'icon' || (!b.mediaType && !hasImage && b.mediaType !== 'none');

                if (hasImage) {
                  return (
                    <div
                      key={b.id || idx}
                      className={`flex flex-col ${isEven ? 'md:flex-row-reverse' : 'md:flex-row'} gap-6 items-center p-3 rounded-lg ${
                        idx < finalBlocks.length - 1 ? 'border-b border-gray-100 pb-6' : ''
                      }`}
                    >
                      <div className="w-full md:w-2/5 flex-shrink-0 aspect-video md:aspect-[4/3] rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
                        <img src={b.image} alt={b.title || `Block ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        {b.title && (
                          <h4 className="text-base font-semibold text-gray-900 mb-2">{b.title}</h4>
                        )}
                        <div className="text-xs text-gray-700 leading-relaxed prose prose-sm max-w-none">
                          {b.body ? (
                            <div dangerouslySetInnerHTML={{ __html: b.body }} />
                          ) : (
                            <span className="italic text-gray-400">Empty block content</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }

                if (hasIcon) {
                  return (
                    <div
                      key={b.id || idx}
                      className={`bg-gray-50/70 border border-gray-200/80 rounded-xl p-4 flex flex-col ${
                        isEven ? 'md:flex-row-reverse' : 'md:flex-row'
                      } gap-4 items-start`}
                    >
                      <div className="w-12 h-12 rounded-xl bg-teal-100/70 text-teal-700 flex items-center justify-center flex-shrink-0 p-2 shadow-2xs border border-teal-200/50">
                        {b.iconType === 'custom' && b.customIcon ? (
                          <img src={b.customIcon} alt={b.title || 'Icon'} className="w-6 h-6 object-contain" />
                        ) : (
                          <i className={`${b.icon || 'ri-star-line'} text-xl`}></i>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        {b.title && (
                          <h4 className="text-sm font-bold text-gray-900 mb-1.5">{b.title}</h4>
                        )}
                        <div className="text-xs text-gray-600 leading-relaxed prose prose-xs max-w-none">
                          {b.body ? (
                            <div dangerouslySetInnerHTML={{ __html: b.body }} />
                          ) : (
                            <span className="italic text-gray-400">Empty block content</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={b.id || idx} className="bg-gray-50/70 border border-gray-200/80 rounded-xl p-4">
                    {b.title && (
                      <h4 className="text-sm font-bold text-gray-900 mb-1.5">{b.title}</h4>
                    )}
                    <div className="text-xs text-gray-600 leading-relaxed prose prose-xs max-w-none">
                      {b.body ? (
                        <div dangerouslySetInnerHTML={{ __html: b.body }} />
                      ) : (
                        <span className="italic text-gray-400">Empty block content</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        }

        // Default: Story layout (Narrative / Journey Timeline Flow)
        if (finalBlocks.length > 1) {
          return (
            <div className="bg-white/80 border border-gray-100 rounded-2xl p-6 md:p-8 shadow-xs max-w-3xl mx-auto relative">
              {/* Connecting Story Timeline Spine */}
              <div className="absolute top-12 bottom-12 left-10 md:left-12 w-0.5 bg-gradient-to-b from-teal-500/50 via-teal-400/30 to-teal-300/10 z-0"></div>

              <div className="space-y-6 relative z-1">
                {finalBlocks.map((b, idx) => {
                  const hasImage = (b.mediaType === 'image' || (!b.mediaType && b.image)) && b.image;
                  const hasIcon = b.mediaType === 'icon' || (!b.mediaType && !hasImage && b.mediaType !== 'none');

                  return (
                    <div key={b.id || idx} className="flex gap-4 md:gap-5 items-start">
                      {/* Timeline Icon / Node */}
                      <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-white border-2 border-teal-500/40 text-teal-700 flex items-center justify-center flex-shrink-0 shadow-sm z-10 p-2">
                        {hasIcon ? (
                          b.iconType === 'custom' && b.customIcon ? (
                            <img src={b.customIcon} alt={b.title || 'Icon'} className="w-6 h-6 object-contain" />
                          ) : (
                            <i className={`${b.icon || 'ri-star-line'} text-xl`}></i>
                          )
                        ) : hasImage ? (
                          <i className="ri-image-2-line text-xl"></i>
                        ) : (
                          <i className="ri-checkbox-circle-line text-xl"></i>
                        )}
                      </div>

                      {/* Story Card Box */}
                      <div className="flex-1 min-w-0 bg-gray-50/90 border border-gray-200/80 rounded-2xl p-5 shadow-2xs hover:shadow-xs transition-shadow">
                        {b.title && (
                          <h4 className="text-base font-bold text-gray-900 mb-2">{b.title}</h4>
                        )}
                        {hasImage && (
                          <div className="aspect-video w-full rounded-xl overflow-hidden bg-gray-100 mb-3 border border-gray-200">
                            <img src={b.image} alt={b.title || 'Story Visual'} className="w-full h-full object-cover" />
                          </div>
                        )}
                        <div className="text-xs text-gray-700 leading-relaxed prose prose-xs max-w-none">
                          {b.body ? (
                            <div dangerouslySetInnerHTML={{ __html: b.body }} />
                          ) : (
                            <span className="italic text-gray-400">Empty block content</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        const first = finalBlocks[0] || { title: '', body: '', image: '', icon: 'ri-star-line', iconType: 'preset', customIcon: '' };
        const hasImage = (first.mediaType === 'image' || (!first.mediaType && first.image)) && first.image;
        const hasIcon = first.mediaType === 'icon' || (!first.mediaType && !hasImage && first.mediaType !== 'none');

        if (hasImage) {
          return (
            <div className="bg-white/80 border border-gray-100 rounded-xl p-6 shadow-xs max-w-3xl mx-auto">
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <div className="w-full md:w-2/5 flex-shrink-0 aspect-video md:aspect-[4/3] rounded-xl overflow-hidden bg-gray-100 border border-gray-200 shadow-xs">
                  <img src={first.image} alt={section.title} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  {first.title && (
                    <h4 className="text-lg font-bold text-gray-900 mb-2">{first.title}</h4>
                  )}
                  {first.body ? (
                    <div
                      className="text-xs text-gray-700 leading-relaxed prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: first.body }}
                    />
                  ) : (
                    <p className="text-xs text-gray-400 italic">No content configured yet. Click "Edit" to add content.</p>
                  )}
                </div>
              </div>
            </div>
          );
        }

        if (hasIcon) {
          return (
            <div className="bg-white/90 border border-gray-200/80 rounded-2xl p-8 shadow-xs text-center max-w-2xl mx-auto">
              <div className="w-16 h-16 rounded-2xl bg-teal-50 border border-teal-200/80 text-teal-700 flex items-center justify-center mx-auto mb-4 shadow-2xs p-2.5">
                {first.iconType === 'custom' && first.customIcon ? (
                  <img src={first.customIcon} alt={first.title || 'Icon'} className="w-8 h-8 object-contain" />
                ) : (
                  <i className={`${first.icon || 'ri-star-line'} text-2xl`}></i>
                )}
              </div>
              {first.title && (
                <h4 className="text-lg font-bold text-gray-900 mb-2">{first.title}</h4>
              )}
              {first.body ? (
                <div
                  className="text-xs text-gray-700 leading-relaxed prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: first.body }}
                />
              ) : (
                <p className="text-xs text-gray-400 italic">No content configured yet. Click "Edit" to add content.</p>
              )}
            </div>
          );
        }

        return (
          <div className="bg-white/80 border border-gray-100 rounded-xl p-6 shadow-xs text-center max-w-2xl mx-auto">
            {first.title && (
              <h4 className="text-lg font-bold text-gray-900 mb-2">{first.title}</h4>
            )}
            {first.body ? (
              <div
                className="text-xs text-gray-700 leading-relaxed prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: first.body }}
              />
            ) : (
              <p className="text-xs text-gray-400 italic">No content configured yet. Click "Edit" to add content.</p>
            )}
          </div>
        );
      }

      default:
        return <p className="text-sm text-gray-600">Content preview</p>;
    }
  };

  return (
    <div className="flex gap-5 h-[calc(100vh-280px)] min-h-[500px]">
      {/* Left Panel - Page List */}
      <div className="w-64 bg-white rounded-xl border border-gray-200 flex-shrink-0 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-100 flex-shrink-0">
          <h3 className="font-serif text-sm text-ink">Pages</h3>
        </div>
        <div className="overflow-y-auto flex-1 custom-scrollbar">
          {deduplicatePages(pages).map(page => {
            const sections = [...page.sections.filter(s => s.type !== 'contactInfo')];
            if (page.pageId === 'home') {
              if (!sections.some(s => s.type === 'gallery')) sections.push({ id: 'gallery-auto', title: 'Gallery Strip', type: 'gallery', content: [], editing: false, enabled: true });
              if (!sections.some(s => s.type === 'blog')) sections.push({ id: 'blog-auto', title: 'Latest Blogs', type: 'blog', content: [], editing: false, enabled: true });
            }
            const isFullyGenerated = sections.every(s => {
              const mandatory = s.type === 'hero' || (page.pageId === 'services' && s.type === 'serviceCard') || (page.pageId === 'products' && s.type === 'productCard') || (page.pageId === 'blog' && s.type === 'blog') || (page.pageId === 'gallery' && s.type === 'gallery') || (page.pageId === 'contact' && (s.type === 'contact' || s.type === 'contactInfo' || s.type === 'contactForm'));
              if (!mandatory) return true;
              if (s.type === 'contactForm' || s.type === 'contact') return true;
              return Array.isArray(s.content) ? s.content.length > 0 : String(s.content).trim().length > 0;
            });
            const computedStatus = isFullyGenerated ? 'Generated' : 'Pending';

            return (
            <button
              key={page.pageId}
              onClick={() => { if (editingSection) toggleEdit(editingSection); setSelectedPageId(page.pageId); }}
              className={`w-full text-left px-4 py-3 flex items-center justify-between gap-2 transition-colors cursor-pointer ${
                selectedPageId === page.pageId
                  ? 'bg-navy/5 border-l-2 border-[#0859b8]'
                  : 'hover:bg-gray-50 border-l-2 border-transparent'
              }`}
            >
              <div className="min-w-0">
                <span className={`text-sm font-medium block truncate ${selectedPageId === page.pageId ? 'text-navy' : 'text-gray-700'}`}>
                  {page.pageName}
                </span>
                <span className={`inline-block text-[10px] mt-0.5 px-1.5 py-0.5 rounded border ${getStatusBadge(computedStatus)}`}>
                  {computedStatus}
                </span>
              </div>
              <i className={`ri-arrow-right-s-line text-gray-300 flex-shrink-0 ${selectedPageId === page.pageId ? 'text-gold' : ''}`}></i>
            </button>
            );
          })}
          {/* Global Footer Button */}
          <button
            onClick={() => { if (editingSection) toggleEdit(editingSection); setSelectedPageId('global-footer'); }}
            className={`w-full text-left px-4 py-3 flex items-center justify-between gap-2 transition-colors cursor-pointer mt-2 border-t border-gray-100 ${
              selectedPageId === 'global-footer'
                ? 'bg-navy/5 border-l-2 border-gold'
                : 'hover:bg-gray-50 border-l-2 border-transparent'
            }`}
          >
            <div className="min-w-0">
              <span className={`text-sm font-medium block truncate ${selectedPageId === 'global-footer' ? 'text-navy' : 'text-gray-700'}`}>
                Footer
              </span>
              <span className={`inline-block text-[10px] mt-0.5 px-1.5 py-0.5 rounded bg-slate-50 text-slate-600 border border-slate-200`}>
                Global
              </span>
            </div>
            <i className={`ri-arrow-right-s-line text-gray-300 flex-shrink-0 ${selectedPageId === 'global-footer' ? 'text-gold' : ''}`}></i>
          </button>
        </div>
      </div>

      {/* Right Panel - Content Canvas */}
      <div ref={contentCanvasRef} className="flex-1 bg-parchment rounded-xl border border-gray-200 p-6 overflow-y-auto relative scroll-smooth">
        {uploadError && (
          <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg max-w-sm mb-4">
            <i className="ri-error-warning-line text-red-500 text-xs"></i>
            <span className="text-xs text-red-600">{uploadError}</span>
          </div>
        )}
        {regenError && (
          <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg max-w-sm mb-4">
            <i className="ri-error-warning-line text-orange-500 text-xs"></i>
            <span className="text-xs text-orange-600">{regenError}</span>
          </div>
        )}
        {selectedPage && (() => {
          const sections = selectedPage.sections;
          const isFullyGenerated = sections.every(s => {
            const mandatory = s.type === 'hero' || (selectedPageId === 'services' && s.type === 'serviceCard') || (selectedPageId === 'products' && s.type === 'productCard') || (selectedPageId === 'blog' && s.type === 'blog') || (selectedPageId === 'gallery' && s.type === 'gallery') || (selectedPageId === 'contact' && (s.type === 'contact' || s.type === 'contactInfo' || s.type === 'contactForm'));
            if (!mandatory) return true;
            if (s.type === 'contactForm') return true;
            return Array.isArray(s.content) ? s.content.length > 0 : String(s.content).trim().length > 0;
          });
          const computedStatus = isFullyGenerated ? 'Generated' : 'Pending';
          const hasHero = sections.some(s => s.type === 'hero');

          const filteredTemplates = SECTION_TEMPLATES.filter(tpl => {
            const matchesCat = selectedCategory === 'All' || tpl.category === selectedCategory;
            const matchesSearch = !sectionSearchQuery ||
              tpl.name.toLowerCase().includes(sectionSearchQuery.toLowerCase()) ||
              tpl.description.toLowerCase().includes(sectionSearchQuery.toLowerCase()) ||
              tpl.type.toLowerCase().includes(sectionSearchQuery.toLowerCase());
            return matchesCat && matchesSearch;
          });

          return (
          <div className="max-w-3xl mx-auto pb-10">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-serif text-xl text-ink">{selectedPage.pageName}</h2>
                <p className="text-xs text-gray-500 mt-1">{sections.length} sections configured · Reorder or customize below</p>
              </div>
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={() => {
                    setSectionSearchQuery('');
                    setSelectedCategory('All');
                    setIsAddSectionModalOpen(true);
                  }}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-300 bg-white hover:border-[#0859b8] hover:text-[#0859b8] text-gray-700 transition-colors flex items-center gap-1.5 cursor-pointer shadow-2xs"
                >
                  <i className="ri-add-line text-sm"></i>
                  Add Section
                </button>
                <button
                  onClick={() => handleGeneratePage(selectedPageId)}
                  disabled={pageLoading[selectedPageId]}
                  className={`text-xs font-medium px-3.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors ${pageLoading[selectedPageId] ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#0859b8] hover:bg-[#0859b8]/90 text-white cursor-pointer'} ${computedStatus === 'Pending' && !pageLoading[selectedPageId] ? 'animate-pulse' : ''}`}
                >
                  <i className={pageLoading[selectedPageId] ? "ri-loader-4-line animate-spin" : "ri-sparkling-line"}></i>
                  {pageLoading[selectedPageId] ? 'Generating...' : (computedStatus === 'Pending' ? 'Generate Page' : 'Regenerate Page')}
                </button>
                <span className={`text-xs px-2.5 py-1 rounded-lg border font-medium ${getStatusBadge(computedStatus)}`}>
                  {computedStatus}
                </span>
              </div>
            </div>

            <div className="space-y-4">
              {sections.map((section, index) => {
                const isHero = section.type === 'hero';
                const mandatory = isHero || (selectedPageId === 'services' && section.type === 'serviceCard') || (selectedPageId === 'products' && section.type === 'productCard') || (selectedPageId === 'blog' && section.type === 'blog') || (selectedPageId === 'gallery' && section.type === 'gallery') || (selectedPageId === 'contact' && (section.type === 'contact' || section.type === 'contactInfo' || section.type === 'contactForm'));
                const isSectionGenerated = Array.isArray(section.content) ? section.content.length > 0 : String(section.content || '').trim().length > 0;
                const isDragging = draggedIndex === index;
                const isDragOver = dragOverIndex === index;

                return (
                <div
                  key={section.id}
                  draggable={!isHero}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  onDrop={(e) => handleDrop(e, index)}
                  className={`bg-white/80 rounded-xl border transition-all duration-150 p-5 shadow-2xs ${
                    isDragging ? 'opacity-30 border-dashed border-[#0859b8] scale-[0.99]' : 'border-gray-200/80'
                  } ${
                    isDragOver && !isHero ? 'border-t-2 border-t-[#0859b8] shadow-md' : ''
                  } ${!mandatory && section.enabled === false ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      {!isHero && (
                        <div
                          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-700 px-1 py-1 -ml-1 rounded transition-colors"
                          title="Drag to reorder section"
                        >
                          <i className="ri-draggable text-lg leading-none"></i>
                        </div>
                      )}
                      {isHero && (
                        <div className="text-[#0859b8] px-1 py-1 -ml-1" title="Hero section is pinned at top">
                          <i className="ri-pushpin-2-line text-sm"></i>
                        </div>
                      )}

                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-semibold text-gray-900">
                            {section.title || SECTION_DISPLAY_NAMES[section.type] || 'Section'}
                          </h3>
                          <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded font-medium">
                            {SECTION_DISPLAY_NAMES[section.type] || section.type}
                          </span>
                          {isHero && (
                            <span className="text-[10px] text-[#0859b8] bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded font-medium flex items-center gap-1">
                              Pinned Top
                            </span>
                          )}
                        </div>
                        {section.subtitle && section.subtitle.trim().toLowerCase() !== (section.title || '').trim().toLowerCase() && (
                          <p className="text-xs text-gray-500 mt-0.5">{section.subtitle}</p>
                        )}
                      </div>

                      {!mandatory && !isHero && (
                        <label className="relative inline-flex items-center cursor-pointer ml-1">
                          <input
                            type="checkbox"
                            checked={section.enabled !== false}
                            onChange={() => toggleSectionEnabled(section.id)}
                            className="sr-only peer"
                          />
                          <div className="w-7 h-4 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-gold/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-[#0859b8]"></div>
                          <span className="ml-1.5 text-[10px] text-gray-500">{section.enabled !== false ? 'Included' : 'Skipped'}</span>
                        </label>
                      )}
                      {mandatory && !isHero && (
                        <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded ml-1">Default</span>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5">
                      {!isHero && (
                        <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden bg-gray-50 mr-1">
                          <button
                            type="button"
                            onClick={() => moveSectionUp(index)}
                            disabled={index <= 1}
                            className={`p-1.5 text-xs transition-colors ${
                              index <= 1
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-600 hover:text-[#0859b8] hover:bg-white cursor-pointer'
                            }`}
                            title={index <= 1 ? 'Cannot move above Hero' : 'Move section up'}
                          >
                            <i className="ri-arrow-up-s-line"></i>
                          </button>
                          <div className="w-px h-3.5 bg-gray-200"></div>
                          <button
                            type="button"
                            onClick={() => moveSectionDown(index)}
                            disabled={index >= sections.length - 1}
                            className={`p-1.5 text-xs transition-colors ${
                              index >= sections.length - 1
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-600 hover:text-[#0859b8] hover:bg-white cursor-pointer'
                            }`}
                            title={index >= sections.length - 1 ? 'Already at bottom' : 'Move section down'}
                          >
                            <i className="ri-arrow-down-s-line"></i>
                          </button>
                        </div>
                      )}

                      {(mandatory || section.enabled !== false) && !(selectedPageId === 'home' && section.type === 'blog') && section.type !== 'contact' && (
                        <button
                          onClick={() => toggleEdit(section.id)}
                          className="text-xs text-gray-600 hover:text-[#0859b8] hover:bg-gray-100 px-2.5 py-1 rounded-md transition-colors flex items-center gap-1 cursor-pointer font-medium"
                        >
                          <i className="ri-pencil-line"></i>
                          {editingSection === section.id ? 'Done' : 'Edit'}
                        </button>
                      )}

                      {(mandatory || section.enabled !== false) && section.type !== 'contact' && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleRegenerate(section.id)}
                            disabled={sectionLoading[section.id]}
                            className={`text-xs flex items-center gap-1 border border-dashed rounded-md px-2.5 py-1 cursor-pointer transition-colors ${sectionLoading[section.id] ? 'border-[#0b7f8f] text-[#0b7f8f] bg-[#0b7f8f]/5' : 'border-gray-300 text-gray-500 hover:text-[#0b7f8f] hover:bg-[#e8f9fb] hover:border-[#e8f9fb]'}`}
                          >
                            <i className={sectionLoading[section.id] ? "ri-loader-4-line animate-spin" : "ri-sparkling-line"}></i>
                            {sectionLoading[section.id] 
                              ? (isSectionGenerated ? 'Regenerating...' : 'Generating...') 
                              : (isSectionGenerated ? 'Regenerate Section' : 'Generate Section')}
                          </button>
                        </div>
                      )}

                      {!isHero && (
                        <button
                          type="button"
                          onClick={() => deleteSection(section.id)}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors cursor-pointer ml-0.5"
                          title="Delete section"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      )}
                    </div>
                  </div>

                  {(mandatory || section.enabled !== false) ? (
                    <>
                      {editingSection === section.id && section.type !== 'contact' && section.type !== 'contactInfo' && (
                        <div className="mb-4 p-3.5 bg-blue-50/40 border border-blue-100 rounded-xl space-y-3">
                          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                            <i className="ri-layout-top-line text-[#0859b8]"></i>
                            <span>
                              Section Heading &amp; Subtitle
                              {(section.type === 'custom' || section.type === 'customSection') && (
                                <span className="text-red-500 ml-1 font-bold">* Compulsory</span>
                              )}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <label className="block text-xs font-medium text-gray-600">
                                  Section Heading
                                  {(section.type === 'custom' || section.type === 'customSection') && (
                                    <span className="text-red-500 font-bold ml-0.5">*</span>
                                  )}
                                </label>
                              </div>
                              <input
                                type="text"
                                className={`w-full text-xs font-medium text-gray-900 rounded-lg px-3 py-1.5 focus:outline-none bg-white border ${getInputClass(section.id, 'section_title', '')}`}
                                value={editValues[section.id]?.section_title ?? section.title ?? ''}
                                onChange={(e) => updateEditValue(section.id, 'section_title', e.target.value, LIMITS.sectionTitle.max)}
                                placeholder="e.g. Services We Offer"
                                maxLength={LIMITS.sectionTitle.max}
                              />
                              <CharLimitLabel current={(editValues[section.id]?.section_title ?? section.title ?? '').length} min={LIMITS.sectionTitle.min} max={LIMITS.sectionTitle.max} />
                              {sectionErrors[section.id]?.section_title && (
                                <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                                  <i className="ri-error-warning-line"></i>
                                  {sectionErrors[section.id].section_title.message}
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Section Subtitle / Description</label>
                              <input
                                type="text"
                                className={`w-full text-xs text-gray-700 rounded-lg px-3 py-1.5 focus:outline-none bg-white border ${getInputClass(section.id, 'section_subtitle', '')}`}
                                value={editValues[section.id]?.section_subtitle ?? section.subtitle ?? section.description ?? ''}
                                onChange={(e) => updateEditValue(section.id, 'section_subtitle', e.target.value, LIMITS.sectionSubtitle.max)}
                                placeholder="e.g. What We Offer or a short overview..."
                                maxLength={LIMITS.sectionSubtitle.max}
                              />
                              <CharLimitLabel current={(editValues[section.id]?.section_subtitle ?? section.subtitle ?? section.description ?? '').length} min={LIMITS.sectionSubtitle.min} max={LIMITS.sectionSubtitle.max} />
                            </div>
                          </div>
                        </div>
                      )}
                      {renderSectionContent(section)}
                    </>
                  ) : (
                    <div className="py-8 text-center">
                      <i className="ri-eye-off-line text-gray-300 text-2xl mb-2"></i>
                      <p className="text-xs text-gray-400">This section is disabled. Toggle the switch above to include it in the output.</p>
                    </div>
                  )}
                </div>
                );
              })}

              <button
                type="button"
                onClick={() => {
                  setSectionSearchQuery('');
                  setSelectedCategory('All');
                  setIsAddSectionModalOpen(true);
                }}
                className="w-full py-3.5 border-2 border-dashed border-gray-300 hover:border-[#0859b8] hover:text-[#0859b8] hover:bg-white rounded-xl text-xs font-semibold text-gray-600 transition-all flex items-center justify-center gap-2 cursor-pointer bg-white/40 shadow-xs"
              >
                <i className="ri-add-circle-line text-base text-[#0859b8]"></i>
                <span>Add Section to {selectedPage.pageName}</span>
              </button>

              {sections.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className={pageLoading[selectedPageId] ? "ri-loader-4-line text-gold animate-spin text-xl" : "ri-file-text-line text-gray-400 text-xl"}></i>
                  </div>
                  <p className="text-sm text-gray-500">
                    {pageLoading[selectedPageId] ? 'Generating your content with AI...' : 'Content pending for this page.'}
                  </p>
                  {!pageLoading[selectedPageId] && (
                    <button 
                      onClick={() => handleGeneratePage(selectedPageId)}
                      className="mt-3 text-sm text-[#0b7f8f] hover:text-[#0b7f8f]/80 cursor-pointer flex items-center gap-1 mx-auto"
                    >
                      Generate with AI →
                    </button>
                  )}
                </div>
              )}
            </div>

            {isAddSectionModalOpen && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                  <div className="p-5 border-b border-gray-100 flex items-center justify-between flex-shrink-0 bg-gray-50/50">
                    <div>
                      <h3 className="font-serif text-lg font-bold text-gray-900">
                        Add Section to {selectedPage.pageName}
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Choose a predesigned section template or create a custom section
                      </p>
                    </div>
                    <button
                      onClick={() => setIsAddSectionModalOpen(false)}
                      className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-close-line text-lg"></i>
                    </button>
                  </div>

                  <div className="p-4 border-b border-gray-100 flex-shrink-0 space-y-3 bg-white">
                    <div className="relative">
                      <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                      <input
                        type="text"
                        className="w-full pl-9 pr-4 py-2 text-xs text-gray-800 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#0859b8] focus:bg-white transition-all"
                        placeholder="Search section templates (e.g. USP, Team, Custom, Testimonials)..."
                        value={sectionSearchQuery}
                        onChange={(e) => setSectionSearchQuery(e.target.value)}
                      />
                      {sectionSearchQuery && (
                        <button
                          onClick={() => setSectionSearchQuery('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                        >
                          <i className="ri-close-circle-fill"></i>
                        </button>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 custom-scrollbar">
                      {['All', 'Features & Content', 'About & Team', 'Products & Services', 'Proof & Trust', 'Contact & Info', 'Custom'].map(cat => (
                        <button
                          key={cat}
                          onClick={() => setSelectedCategory(cat)}
                          className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                            selectedCategory === cat
                              ? 'bg-[#0859b8] text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="p-5 overflow-y-auto flex-1 custom-scrollbar bg-gray-50/40">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                      {filteredTemplates.map(tpl => {
                        const isHeroDisabled = tpl.type === 'hero' && hasHero;
                        return (
                          <div
                            key={tpl.type}
                            className={`p-4 rounded-xl border text-left transition-all ${
                              isHeroDisabled
                                ? 'bg-gray-100/70 border-gray-200 opacity-60 cursor-not-allowed'
                                : 'bg-white border-gray-200 hover:border-[#0859b8] hover:shadow-md cursor-pointer group'
                            }`}
                            onClick={() => {
                              if (!isHeroDisabled) {
                                handleAddSection(tpl);
                              }
                            }}
                          >
                            <div className="flex items-start gap-3.5">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                tpl.type === 'custom'
                                  ? 'bg-purple-50 text-purple-600'
                                  : 'bg-blue-50 text-[#0859b8]'
                              }`}>
                                <i className={`${tpl.icon} text-lg`}></i>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <h4 className="text-sm font-semibold text-gray-900 group-hover:text-[#0859b8] transition-colors truncate">
                                    {tpl.name}
                                  </h4>
                                  <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium flex-shrink-0">
                                    {tpl.category}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                                  {tpl.description}
                                </p>
                                {isHeroDisabled ? (
                                  <span className="inline-block text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded mt-2.5 font-medium">
                                    Page already has a Hero (Max 1)
                                  </span>
                                ) : (
                                  <div className="mt-3 flex items-center text-xs font-semibold text-[#0859b8] opacity-0 group-hover:opacity-100 transition-opacity">
                                    <span>+ Add to Page</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {filteredTemplates.length === 0 && (
                      <div className="py-12 text-center">
                        <i className="ri-search-line text-gray-300 text-3xl mb-2"></i>
                        <p className="text-sm text-gray-500 font-medium">No section templates found</p>
                        <p className="text-xs text-gray-400 mt-1">Try a different search keyword or category filter</p>
                      </div>
                    )}
                  </div>

                  <div className="p-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0 bg-white">
                    <span className="text-xs text-gray-400">
                      {filteredTemplates.length} templates available
                    </span>
                    <button
                      onClick={() => setIsAddSectionModalOpen(false)}
                      className="px-4 py-1.5 text-xs text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          );
        })()}
        
        {selectedPageId === 'global-footer' && (
          <div className="max-w-3xl mx-auto pb-10">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-serif text-xl text-ink">Footer</h2>
                <p className="text-xs text-gray-500 mt-1">Manage global footer content</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Brand Description */}
              <div className="bg-white/60 rounded-xl border border-gray-200/60 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900">Description</h3>
                  <button
                    onClick={handleRegenerateGlobalFooter}
                    disabled={sectionLoading['global-footer']}
                    className="text-xs text-[#0b7f8f] hover:text-[#0b7f8f]/80 cursor-pointer flex items-center gap-1"
                  >
                    <i className={sectionLoading['global-footer'] ? "ri-loader-4-line animate-spin" : "ri-sparkling-line"}></i>
                    {sectionLoading['global-footer'] ? 'Generating...' : 'Generate Description'}
                  </button>
                </div>
                <div className="space-y-3">
                  <textarea
                    className="w-full text-sm text-gray-700 rounded-lg px-3 py-2 border border-gray-300 focus:border-gold focus:outline-none resize-none"
                    rows={4}
                    value={globalContactInfo.description}
                    onChange={(e) => setGlobalContactInfo(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Short description of your brand..."
                  />
                  <CharLimitLabel current={globalContactInfo.description.length} min={LIMITS.contact.min} max={LIMITS.contact.max} />
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-white/60 rounded-xl border border-gray-200/60 p-5">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Email</label>
                    <input
                      type="email"
                      pattern="[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$"
                      className="w-full text-sm text-gray-900 rounded-lg px-3 py-2 border border-gray-300 focus:border-gold focus:outline-none invalid:border-red-400 focus:invalid:border-red-400 peer"
                      value={globalContactInfo.email}
                      onChange={(e) => setGlobalContactInfo(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="hello@company.com"
                    />
                    <p className="mt-1.5 text-xs text-red-500 hidden peer-invalid:block">Please enter a valid email address</p>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Phone Number</label>
                    <input
                      type="text"
                      pattern="^\d{10}$"
                      className="w-full text-sm text-gray-900 rounded-lg px-3 py-2 border border-gray-300 focus:border-gold focus:outline-none invalid:border-red-400 focus:invalid:border-red-400 peer"
                      value={globalContactInfo.phone}
                      onChange={(e) => setGlobalContactInfo(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="1234567890"
                    />
                    <p className="mt-1.5 text-xs text-red-500 hidden peer-invalid:block">Phone number must contain exactly 10 digits</p>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Business Location</label>
                    <input
                      type="text"
                      className="w-full text-sm text-gray-900 rounded-lg px-3 py-2 border border-gray-300 focus:border-gold focus:outline-none"
                      value={globalContactInfo.location}
                      onChange={(e) => setGlobalContactInfo(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="123 Business Ave, Suite 100, City, ST 12345"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {blogEditor.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl border border-gray-200 shadow-lg w-full max-w-2xl mx-4 p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900">
                {blogEditor.postIndex !== null && blogEditor.postIndex >= 0 ? 'Edit Blog Post' : 'New Blog Post'}
              </h3>
              <button
                onClick={() => setBlogEditor({ open: false, sectionId: null, postIndex: null, title: '', image: '', date: '', content: '' })}
                className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 transition-colors cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Title</label>
                <input
                  className="w-full text-sm text-gray-900 rounded-lg px-3 py-2 border border-gray-300 focus:border-gold focus:outline-none"
                  value={blogEditor.title}
                  onChange={(e) => setBlogEditor(prev => ({ ...prev, title: e.target.value }))}
                  maxLength={LIMITS.blogTitle.max}
                  placeholder="Post title"
                />
                <CharLimitLabel current={blogEditor.title.length} min={LIMITS.blogTitle.min} max={LIMITS.blogTitle.max} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Date</label>
                <input
                  className="w-full text-sm text-gray-900 rounded-lg px-3 py-2 border border-gray-300 focus:border-gold focus:outline-none"
                  value={blogEditor.date}
                  onChange={(e) => setBlogEditor(prev => ({ ...prev, date: e.target.value }))}
                  maxLength={LIMITS.blogDate.max}
                  placeholder="e.g., May 5, 2025"
                />
                <CharLimitLabel current={blogEditor.date.length} min={LIMITS.blogDate.min} max={LIMITS.blogDate.max} />
              </div>
              <div className="mb-5">
                <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wider">Cover Image</label>
                {blogEditor.image && (
                  <div className="mb-4 aspect-video bg-gray-50 rounded-lg border border-gray-100 overflow-hidden">
                    <img src={blogEditor.image} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}
                <ImageUploader 
                  type="blog" 
                  currentImage={blogEditor.image} 
                  onUpload={(file, url) => setBlogEditor(prev => ({ ...prev, image: url }))} 
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Full Content</label>
                <RichTextEditor
                  html={blogEditor.content}
                  onChange={(html) => setBlogEditor(prev => ({ ...prev, content: html }))}
                  placeholder="Write the full blog post content here..."
                  minHeight="280px"
                />
                <CharLimitLabel current={blogEditor.content.length} min={LIMITS.blogContent.min} max={LIMITS.blogContent.max} />
              </div>
            </div>
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
              <div className="text-[11px] text-gray-400 flex items-center gap-1">
                <i className="ri-information-line"></i>
                HTML formatting supported
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setBlogEditor({ open: false, sectionId: null, postIndex: null, title: '', image: '', date: '', content: '' })}
                  className="text-xs text-gray-500 hover:text-gray-700 px-3 py-2 rounded cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={saveBlogEditor}
                  disabled={blogEditor.title.length < LIMITS.blogTitle.min || blogEditor.date.length < LIMITS.blogDate.min || blogEditor.content.length < LIMITS.blogContent.min}
                  className="text-xs font-medium px-4 py-2 rounded bg-[#0b7f8f] text-white hover:bg-[#0b7f8f]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  Save Post
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
